import { listAllMonths, projectStatus } from "@/constants/constants";
import {
  CategoryProjectType,
  ParcelType,
  ParcelUpdateType,
  ProjectType,
  ProjectUpdateType,
} from "@/types";
import { ProjectIndicators } from "@/types/dashboard";
import { Indicator } from "@/types/pp";
import { type ClassValue, clsx } from "clsx";
import slugify from "slugify";
import { twMerge } from "tailwind-merge";
import { convertToEuro } from "./amount";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const convertLength = (
  meters: number,
  unit: string,
  decimals: number = 1
): number => {
  let result: number;

  switch (unit) {
    case "m":
      result = meters;
      break;
    case "cm":
      result = meters * 100;
      break;
    case "km":
      result = meters / 1_000;
      break;
    case "nautical_mile":
      result = meters / 1_852;
      break;
    default:
      result = meters;
  }

  return parseFloat(result.toFixed(decimals));
};
export const groupByFirstLetter = (items: CategoryProjectType[]) => {
  const copyItems = [...items];
  return copyItems.reduce((acc, item) => {
    const firstLetter = item.name[0].toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(item);
    return acc;
  }, {} as Record<string, CategoryProjectType[]>);
};

interface FileValidationOptions {
  allowedTypes: string[];
  maxSizeMB: number; // Max file size in MB
}

export function validateFiles(files: FileList, options: FileValidationOptions) {
  const { allowedTypes, maxSizeMB } = options;
  const validFiles: File[] = [];
  const previewUrls: string[] = [];

  Array.from(files).forEach((file) => {
    if (!allowedTypes.includes(file.type)) {
      alert(`Seuls les formats ${allowedTypes.join(", ")} sont autorisés.`);
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(
        `La taille de chaque fichier doit être inférieure à ${maxSizeMB} Mo.`
      );
      return;
    }
    validFiles.push(file);
    previewUrls.push(URL.createObjectURL(file));
  });

  return { validFiles, previewUrls };
}
type dataParcel = {
  properties: {
    code_arr: string;
    code_com: string;
    code_dep: string;
    code_insee: string;
    com_abs: string;
    contenance: string;
    feuille: string;
    idu: string;
    nom_com: string;
    numero: string;
    section: string;
  };
  geometry: {
    coordinates: [];
  };
};
export function dataProcessingParcel(
  data: dataParcel,
  surface: number,
  isFrench: boolean
) {
  const processedData = {
    codeArr: data.properties.code_arr,
    codeCom: data.properties.code_com,
    codeDep: data.properties.code_dep,
    codeInsee: data.properties.code_insee,
    comAbs: data.properties.com_abs,
    contenance: data.properties.contenance,
    feuille: data.properties.feuille,
    idu: data.properties.idu,
    nomCom: data.properties.nom_com,
    numero: data.properties.numero,
    section: data.properties.section,
    coordinates: data.geometry.coordinates,
    // surface: surface,
    pays: isFrench ? "France" : "Autres",
  };
  return processedData;
}

export const checkIfInFrance = (lat: number, lng: number) => {
  // Les coordonnées définissent les limites approximatives de la France
  const bounds = {
    north: 51.124199,
    south: 41.342327,
    west: -5.1406,
    east: 9.6625,
  };
  return (
    lat >= bounds.south &&
    lat <= bounds.north &&
    lng >= bounds.west &&
    lng <= bounds.east
  );
};

// Custom debounce function
// eslint-disable-next-line @typescript-eslint/ban-types
export function debounce(func: Function, wait: number) {
  let timeout: ReturnType<typeof setTimeout>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function (...args: any[]) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function getInitialCoordinates(position: GeolocationPosition) {
  const posLocalStorage = localStorage.getItem("loc_map");

  if (posLocalStorage) {
    try {
      const parsedPosLocalStorage = JSON.parse(posLocalStorage);
      if (
        Array.isArray(parsedPosLocalStorage) &&
        parsedPosLocalStorage.length === 3
      ) {
        return {
          latitude: parseFloat(parsedPosLocalStorage[0]),
          longitude: parseFloat(parsedPosLocalStorage[1]),
          zoom: parseInt(parsedPosLocalStorage[2]),
        };
      } else {
        throw new Error("Invalid format in localStorage.");
      }
    } catch (error) {
      console.error("Error parsing localStorage:", error);
    }
  }

  // Fallback to the Geolocation API position
  return {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    zoom: 20, // Default zoom level
  };
}

export function getProjectTypeFields(projectTypes, projectType) {
  const fields = [];
  projectTypes.map(function (pt) {
    const slug = slugify(pt.name, {
      lower: true,
      remove: /[*+~.()'"!:@]/g,
    });
    if (slug === projectType) {
      fields.push(pt.fields.map((field) => field.name));
    }
  });
  if (fields.length) {
    return fields[0];
  }
  return [];
}

export function getProjectTypeId(projectTypes, projectType): number | null {
  const fields = [];
  for (let i = 0; i < projectTypes.length; i++) {
    const slug = slugify(projectTypes[i].name, {
      lower: true,
      remove: /[*+~.()'"!:@]/g,
    });
    if (slug === projectType) return projectTypes[i].id;
  }

  return null;
}
export function convertStringToNumber(str: string | undefined): number | null {
  if (str === null) {
    return null;
  }

  const num = parseFloat(str);
  return isNaN(num) ? null : num;
}

export function dataProcessingProject(data): ProjectUpdateType {
  const processedData: ProjectUpdateType = {
    id: data.id !== undefined ? data.id : null,
    name: data.name,
    type: data.type,
    description: data.description,
    fundRecipient:
      data.fundRecipient !== undefined && data.fundRecipient.name !== ""
        ? data.fundRecipient
        : null,
    fundLocal:
      data.fundLocal !== undefined && data.fundLocal.name !== ""
        ? data.fundLocal
        : null,
    manager:
      data.manager !== undefined &&
        (data.manager.firstname !== "" ||
          data.manager.lastname !== "" ||
          data.manager.email !== "" ||
          data.manager.society !== "")
        ? data.manager
        : null,
    location:
      data.location !== undefined && data.location.length
        ? data.location.map((loc) => loc.map((l) => [l.lat, l.lng]))
        : null,
    launchDate: data.launchDate,
    expectedDuration: data.expectedDuration,
    desiredFunding:
      data.desiredFunding !== undefined && data.desiredFunding.length
        ? data.desiredFunding.filter((df) => df.tile !== "" && df.amount !== 0)
          .length
          ? data.desiredFunding.filter(
            (df) => df.tile !== "" && df.amount !== 0
          )
          : null
        : null,
    surface: data.surface,
    linear: data.linear,
    trees: data.trees,
    sau: data.sau,
    peopleInvolved: data.peopleInvolved,
    protectedSpecies:
      data.protectedSpecies !== undefined && data.protectedSpecies.length
        ? data.protectedSpecies.filter((ps) => ps.name !== "").length
          ? data.protectedSpecies.filter((ps) => ps.name !== "")
          : null
        : null,
    plantedSpecies:
      data.plantedSpecies !== undefined && data.plantedSpecies.length
        ? data.plantedSpecies.filter((ps) => ps.name !== "").length
          ? data.plantedSpecies.filter((ps) => ps.name !== "")
          : null
        : null,
    parcels:
      data.parcel !== undefined && data.parcel.length
        ? data.parcel.map((p: ParcelType) => {
          const pf: ParcelUpdateType = {
            codeArr: p.codeArr,
            codeCom: p.codeCom,
            codeDep: p.codeDep,
            codeInsee: p.codeInsee,
            comAbs: p.comAbs,
            contenance: p.contenance,
            feuille: p.feuille,
            idu: p.idu,
            nomCom: p.nomCom,
            numero: p.numero,
            section: p.section,
            coordinates: p.coordinates[0][0],
          };

          return pf;
        })
        : null,
    files: data.files !== undefined && data.files.length ? data.files : null,
  };

  return processedData;
}

export const socialFields = [
  "plannedAidAmount",
  "plannedArticlesToPublish",
  "plannedBeneficiaries",
  "plannedCarbonFootprintReduction",
  "plannedCenters",
  "plannedCollectedTextiles",
  "plannedCommonProjects",
  "plannedCultivatedArea",
  "plannedDistributions",
  "plannedEvents",
  "plannedFundsToCollect",
  "plannedHarvests",
  "plannedHours",
  "plannedMeals",
  "plannedObtainingTime",
  "plannedPartnerships",
  "plannedPeopleToOrient",
  "plannedProcessingTime",
  "plannedRecoveredVolume",
  "plannedRecyclingVolume",
  "plannedSponsor",
  "plannedTrainings",
  "plannedConsultations",
  "availableBedNumber",
];

function checkStateStep2(data: ProjectType) {
  if (
    data.name !== "" &&
    data.country !== "" &&
    data.country !== undefined &&
    data.country !== null
  )
    return true;
  return false;
}

function checkStateStep3(data: ProjectType) {
  if (data.launchDate) return true;
  return false;
}

function checkStateStep4(data: ProjectType) {
  if (
    (data.desiredFunding && data.desiredFunding.length > 0) ||
    (data.obtainedFunding && data.obtainedFunding.length > 0)
  )
    return true;

  return checkStateStep5(data);
}

function checkStateStep5(data: ProjectType) {
  const typesProject = JSON.parse(localStorage.getItem("typesProject") || "[]");
  const fields = getProjectTypeFields(
    typesProject,
    slugify(data.type.name, {
      lower: true,
      remove: /[*+~.()'"!:@]/g,
    })
  );

  const fieldsInStep = fields.filter(
    (field) =>
      field === "surface" ||
      field === "trees" ||
      field === "sau" ||
      field === "linear" ||
      field === "peopleInvolved" ||
      field === "protectedSpecies" ||
      field === "plantedSpecies" ||
      socialFields.includes(field)
  );

  if (fieldsInStep.length === 0) {
    return checkStateStep6(data);
  }

  if (
    (data.surface === 0 || data.surface === null) &&
    fields.includes("surface")
  ) {
    return false;
  }

  if (
    (data.peopleInvolved === 0 || data.peopleInvolved === null) &&
    fields.includes("peopleInvolved")
  ) {
    return false;
  }

  if ((data.sau === 0 || data.sau === null) && fields.includes("sau")) {
    return false;
  }

  if (
    (data.linear === 0 || data.linear === null) &&
    fields.includes("linear")
  ) {
    return false;
  }

  return true;
}

function checkStateStep6(data: ProjectType) {
  if (
    (data.parcel === null ||
      data.parcel === undefined ||
      data.parcel.length <= 0) &&
    (data.location === null ||
      data.location === undefined ||
      data.location.length <= 0)
  ) {
    return false;
  }

  return true;
}

function checkStateStep7(data: ProjectType) {
  if (data.mediaDescription && data.mediaDescription.length > 0) {
    return true;
  }

  return checkStateStep8(data);
}

function checkStateStep8(data: ProjectType) {
  if (data.mediaPhotos && data.mediaPhotos.length > 0) {
    return true;
  }

  return (
    data.status !== projectStatus.NotStarted &&
    data.status !== projectStatus.InProgress
  );
}

function setStepStates(data: ProjectType) {
  const stepStates = {
    2: checkStateStep2(data),
    3: checkStateStep3(data),
    4: checkStateStep4(data),
    5: checkStateStep5(data),
    6: checkStateStep6(data),
    7: checkStateStep7(data),
    8: checkStateStep8(data),
  };

  return stepStates;
}

export function reverseDataProcessingProject(data: ProjectType): ProjectType {
  const stepStates = setStepStates(data);
  const socialFieldsValues = {
    plannedAidAmount: data.plannedAidAmount,
    plannedArticlesToPublish: data.plannedArticlesToPublish,
    plannedBeneficiaries: data.plannedBeneficiaries,
    plannedCarbonFootprintReduction: data.plannedCarbonFootprintReduction,
    plannedCenters: data.plannedCenters,
    plannedCollectedTextiles: data.plannedCollectedTextiles,
    plannedCommonProjects: data.plannedCommonProjects,
    plannedCultivatedArea: data.plannedCultivatedArea,
    plannedDistributions: data.plannedDistributions,
    plannedEvents: data.plannedEvents,
    plannedFundsToCollect: data.plannedFundsToCollect,
    plannedHarvests: data.plannedHarvests,
    plannedHours: data.plannedHours,
    plannedMeals: data.plannedMeals,
    plannedObtainingTime: data.plannedObtainingTime,
    plannedPartnerships: data.plannedPartnerships,
    plannedPeopleToOrient: data.plannedPeopleToOrient,
    plannedProcessingTime: data.plannedProcessingTime,
    plannedRecoveredVolume: data.plannedRecoveredVolume,
    plannedRecyclingVolume: data.plannedRecyclingVolume,
    plannedSponsor: data.plannedSponsor,
    plannedTrainings: data.plannedTrainings,
    plannedConsultations: data.plannedConsultations,
    availableBedNumber: data.availableBedNumber,
  };

  const res: ProjectType = {
    name: data.name,
    description: data.description,
    type: slugify(data.type.name, {
      lower: true,
      remove: /[*+~.()'"!:@]/g,
    }),
    country: data.country,
    defaultImage: data.defaultImage,
    fundRecipient: data.fundRecipient
      ? {
        name: data.fundRecipient.name,
        email: data.fundRecipient.email,
      }
      : {
        name: "",
        email: "",
      },
    fundLocal: data.fundLocal
      ? {
        name: data.fundLocal.name,
        uri: data.fundLocal.uri,
      }
      : {
        name: "",
        uri: "",
      },
    manager: data.manager
      ? {
        logo: data.manager.logo,
        firstname: data.manager.firstname,
        lastname: data.manager.lastname,
        email: data.manager.email,
        society: data.manager.society,
        phone: data.manager.phone,
      }
      : {
        logo: null,
        firstname: "",
        lastname: "",
        email: "",
        society: "",
      },
    id: data.id,
    launchDate: data.launchDate,
    expectedDuration: data.expectedDuration,
    objective: data.objective,
    actions: data.actions,
    benefits: data.benefits,
    criteria: data.criteria,
    desiredFunding:
      data.desiredFunding && data.desiredFunding.length
        ? data.desiredFunding.map((df) => {
          return {
            id: df.id,
            title: df.title,
            amount: df.amount,
            devise: df.devise,
            media: df.media,
          };
        })
        : [],
    obtainedFunding:
      data.obtainedFunding && data.obtainedFunding.length
        ? data.obtainedFunding.map((df) => {
          return {
            id: df.id,
            partner: df.partner,
            title: df.title,
            amount: df.amount,
            devise: df.devise,
            media: df.media,
          };
        })
        : [],
    surface: data.surface ?? 0,
    trees: data.trees ?? 0,
    sau: data.sau ?? 0,
    linear: data.longHaie ?? 0,
    peopleInvolved: data.peopleInvolved ?? 0,
    protectedSpecies:
      data.protectedSpecies && data.protectedSpecies.length
        ? data.protectedSpecies
          .map((sp) => {
            return {
              name: sp.species.name,
            };
          })
          .filter(
            (obj, index, self) =>
              index === self.findIndex((o) => o.name === obj.name)
          )
        : [
          {
            name: "",
          },
        ],
    plantedSpecies:
      data.plantedSpecies && data.plantedSpecies.length
        ? data.plantedSpecies
          .map((sp) => {
            return {
              name: sp.species.name,
            };
          })
          .filter(
            (obj, index, self) =>
              index === self.findIndex((o) => o.name === obj.name)
          )
        : [
          {
            name: "",
          },
        ],
    location:
      data.location && data.location.length
        ? data.location.map((l) => {
          return l.map((p) => {
            return {
              lat: p[0],
              lng: p[1],
            };
          });
        })
        : [],
    parcel: data.parcel
      ? data.parcel.map((p) => {
        return {
          ...p,
          contenance: parseFloat(p.contenance),
          feuille: parseInt(p.feuille),
          coordinates: [[p.coordinates]],
          pays: "France",
        };
      })
      : [],
    mediaDescription: data.mediaDescription ? data.mediaDescription : [],
    mediaPhotos: data.mediaPhotos ? data.mediaPhotos : [],
    status: data.status ?? projectStatus.NotStarted,
    ...socialFieldsValues,
    stepStates,
  };

  return res;
}

export function addMonths(startDate: Date | undefined, monthsToAdd: number) {
  if (!startDate) return null;

  const newDate = new Date(startDate);

  newDate.setMonth(newDate.getMonth() + (monthsToAdd - 1));

  return newDate;
}

const getIndicatorCumulativeOption = (
  indicator: {
    cumulative?: boolean;
    cumulativeProgram?: boolean;
    cumulativeDashboard?: boolean;
    cumulativeReporting?: boolean;
  },
  context?: "dashboard" | "project" | "programme" | null
) => {
  if (context === "dashboard") {
    return Boolean(
      indicator.cumulativeDashboard ?? false


    );
  }

  if (context === "programme") {
    return Boolean(
      indicator.cumulativeProgram ??
      false
    );
  }

  return Boolean(indicator.cumulativeReporting ?? false);
};

export function getChartDataMonth(
  reportingIndicatorValues: ProjectIndicators[],
  currentYear: number,
  indicator: any,
  context?: "dashboard" | "project" | "programme" | null
) {
  const getIndicatorCount = (item: { count?: number | null }) => {
    const count = Number(item?.count ?? 1);
    return Number.isFinite(count) && count > 0 ? count : 1;
  };
  const cY = new Date().getFullYear();
  const cM = new Date().getMonth();
  const MONTH_CUMUL_SENTINEL_YEAR = 1000;

  const getMonthCumulLimit = (rawDate: string | null | undefined) => {
    if (!rawDate) {
      return { month: null, year: null };
    }

    const raw = String(rawDate);
    const match = raw.match(/(\d{4})-(\d{2})/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);

      return {
        month: Number.isNaN(month) ? null : month,
        year:
          Number.isNaN(year) || year === MONTH_CUMUL_SENTINEL_YEAR ? null : year,
      };
    }

    const monthOnlyMatch = raw.match(/^\d{1,2}$/);
    if (monthOnlyMatch) {
      const month = Number(monthOnlyMatch[0]);

      return {
        month: Number.isNaN(month) ? null : month,
        year: null,
      };
    }

    return { month: null, year: null };
  };

  const shouldCumulateForMonth = (
    ind: {
      cumulative?: boolean;
      cumulativeProgram?: boolean
      cumulativeDashboard?: boolean
      cumulativeReporting?: boolean
      cumulativeMonth?: boolean;
      dateCumulMonth?: string | null;
    },
    targetYear: number,
    targetMonth: number, // mois courant, de 1 à 12,
    context?: "dashboard" | "project" | "programme" | null
  ): boolean => {
    const cumulOptions = context == "dashboard" ? indicator.cumulativeDashboard : context == "programme" ? indicator.cumulativeProgram : indicator.cumulativeReporting;

    if (!cumulOptions) {

      // Règle 4 : cumulative est faux → jamais de cumul
      return false;
    }

    if (cumulOptions && !ind.cumulativeMonth) {
      // Règle 3 : cumulative=true, cumulativeMonth=false → cumul jusqu'à la fin
      return true;
    }

    // À partir d'ici : cumulative=true ET cumulativeMonth=true
    const { month: endCumulMonth, year: endCumulYear } = getMonthCumulLimit(
      ind.dateCumulMonth
    );

    // Si pas de mois de fin défini → cumul jusqu'à la fin par défaut
    if (!endCumulMonth || Number.isNaN(endCumulMonth)) {
      return true;
    }

    // Logique principale
    if (!endCumulYear) {
      // pas d'année définie
      return endCumulMonth > targetMonth;
    }

    // Si année définie
    if (endCumulYear > targetYear) {
      return true; // cumul encore valable
    } else if (endCumulYear === targetYear) {
      return endCumulMonth >= targetMonth;
    } else {
      // endCumulYear < targetYear
      return false; // on est déjà passé
    }
  };
  // 1️⃣ Initialiser les mois
  const chartDataMonth = listAllMonths.map((month) => ({
    legend: month,
    prevu: 0,
    realise: 0,
    verifie: 0,
    compte: [0, 0, 0],
    moyenne: false,
    cummulative: false,
    valueWithoutCumul: [null, null, null],
  }));
  const indicatorYears = reportingIndicatorValues
    .filter(
      (item) =>
        item.indicator.id === indicator.id &&
        item.year !== null &&
        item.year !== undefined
    )
    .map((item) => Number(item.year))
    .filter((year) => !Number.isNaN(year));
  const earliestIndicatorYear =
    indicatorYears.length > 0 ? Math.min(...indicatorYears) : currentYear;
  const previousYearEndData =
    currentYear - 1 >= earliestIndicatorYear
      ? getChartDataMonth(
        reportingIndicatorValues,
        currentYear - 1,
        indicator,
        context
      )[11]
      : null;

  // 2️⃣ Calcul du cumul de l’année précédente
  const previousYearCompte = [0, 0, 0];
  const cumulOptions = context == "dashboard" ? indicator.cumulativeDashboard : context == "programme" ? indicator.cumulativeProgram : indicator.cumulativeReporting;
  if (cumulOptions) {
    reportingIndicatorValues
      .filter(
        (item) =>
          item.year === currentYear - 1 &&
          item.indicator.id === indicator.id &&
          item.valuewithoutCumul != null
      )
      .forEach((item) => {
        const count = getIndicatorCount(item as any);
        if (item.type === 1) previousYearCompte[0] += count;
        if (item.type === 2) previousYearCompte[1] += count;
        if (item.type === 3) previousYearCompte[2] += count;
      });
  }

  // 3️⃣ Boucle mensuelle
  chartDataMonth.forEach((monthData, index) => {
    const shouldCumulateActualsForYear = currentYear <= cY;
    const filteredIndicators = reportingIndicatorValues.filter(
      (item) =>
        item.year === currentYear &&
        item.month === index &&
        item.indicator.id === indicator.id
    );
    const previousMonthIndicators = index == 0 ? reportingIndicatorValues.filter(
      (item) =>
        item.year === currentYear - 1 &&
        item.month === 11 &&
        item.indicator.id === indicator.id
    ) : reportingIndicatorValues.filter(
      (item) =>
        item.year === currentYear &&
        item.month === index - 1 &&
        item.indicator.id === indicator.id
    );

    const prevMonthData = chartDataMonth[index - 1];
    const isCumulativeMonth = shouldCumulateForMonth(
      indicator,
      currentYear,
      index + 1,
      context
    );
    // 🔑 Initialisation correcte du cumul
    if (isCumulativeMonth) {
      if (prevMonthData) {
        monthData.compte = [...prevMonthData.compte];
        if (!shouldCumulateActualsForYear) {
          monthData.compte[1] = 0;
          monthData.compte[2] = 0;
        }
      } else {
        // Janvier → héritage de décembre année précédente
        monthData.compte = previousYearEndData
          ? [...previousYearEndData.compte]
          : [...previousYearCompte];
        if (!shouldCumulateActualsForYear) {
          monthData.compte[1] = 0;
          monthData.compte[2] = 0;
        }
        monthData.prevu = previousYearEndData?.prevu ?? monthData.prevu;
        if (shouldCumulateActualsForYear) {
          monthData.realise = previousYearEndData?.realise ?? monthData.realise;
          monthData.verifie = previousYearEndData?.verifie ?? monthData.verifie;
        }
      }
    } else {
      monthData.compte = [0, 0, 0];
    }
    // if (indicator.id == 1013) console.log("isCumulativeMonth", isCumulativeMonth)
    if ((isCumulativeMonth && prevMonthData)) {
      monthData.prevu = prevMonthData.prevu;
      if (shouldCumulateActualsForYear) {
        monthData.realise = prevMonthData.realise;
        monthData.verifie = prevMonthData.verifie;
      } else {
        monthData.realise = 0;
        monthData.verifie = 0;
      }
    }
    // 4️⃣ Traitement des valeurs du mois
    filteredIndicators.forEach((item) => {
      if (item.valuewithoutCumul == null) return;
      const cumulOptions = context == "dashboard" ? item.indicator.cumulativeDashboard : context == "programme" ? item.indicator.cumulativeProgram : item.indicator.cumulativeReporting;

      monthData.cummulative = cumulOptions ?? false;
      monthData.moyenne = item.indicator.moyenne ?? false;
      const currentValue = item.value ?? 0;
      const monthlyValue =
        !cumulOptions || (cumulOptions &&
          item.indicator.cumulativeMonth &&
          !isCumulativeMonth)
          ? currentValue
          : item.type === 1
            ? monthData.prevu + currentValue
            : item.type === 2 && shouldCumulateActualsForYear
              ? monthData.realise + currentValue
              : item.type === 3 && shouldCumulateActualsForYear
                ? monthData.verifie + currentValue
                : currentValue;

      // PREVU
      if (item.type === 1) {
        monthData.valueWithoutCumul[0] =
          (monthData.valueWithoutCumul[0] ?? 0) + item.valuewithoutCumul;
        monthData.prevu = monthlyValue;
        monthData.compte[0] += getIndicatorCount(item as any);

        if (cumulOptions && item.indicator.moyenne) {
          monthData.prevu = monthlyValue / monthData.compte[0];
        }
      }

      // REALISE
      if (item.type === 2) {
        if (item.year > cY || (item.year === cY && item.month > cM)) {
          monthData.realise = 0;
        } else {
          monthData.valueWithoutCumul[1] =
            (monthData.valueWithoutCumul[1] ?? 0) + item.valuewithoutCumul;
          monthData.realise = monthlyValue;
          monthData.compte[1] += getIndicatorCount(item as any);

          if (cumulOptions && item.indicator.moyenne) {
            monthData.realise = monthlyValue / monthData.compte[1];
          }
        }
      }

      // VERIFIE
      if (item.type === 3) {
        if (item.year > cY || (item.year === cY && item.month > cM)) {
          monthData.verifie = 0;
        } else {
          monthData.valueWithoutCumul[2] =
            (monthData.valueWithoutCumul[2] ?? 0) + item.valuewithoutCumul;

          monthData.verifie = monthlyValue;
          monthData.compte[2] += getIndicatorCount(item as any);

          if (cumulOptions && item.indicator.moyenne) {
            monthData.verifie = monthlyValue / monthData.compte[2];
          }
        }
      }
    });
  });
  return chartDataMonth;
}

export function getChartDataYear(
  reportingIndicatorValues: {
    id: number;
    indicator: object;
    month: number;
    type: number;
    value: number;
  }[],
  indicator: Indicator,
  expectedDurationYear: number,
  launchDate: number,
  minDate: any,
  maxDate: any,
  context?: "dashboard" | "project" | "programme" | null
) {
  // const isGlobalContext = false;
  const isGlobalContext = context != "project";



  // Générer un tableau d'années
  let arrYear = generateYearArray(expectedDurationYear);
  if (minDate && maxDate) {
    arrYear = [];
    for (let i = minDate; i <= maxDate; i++) {
      arrYear.push(i);
    }
  } else if (Array.isArray(minDate) && indicator.code === "tco2_sequestrees") {
    let tco2Year = 0;
    // minDate is parameters
    minDate.forEach((p) => {
      if (p.name === "tco2_year") {
        tco2Year = parseInt(p.value);
      }
    });

    if (tco2Year) {
      arrYear = [];
      for (let i = launchDate; i < launchDate + tco2Year; i++) {
        arrYear.push(i);
      }
    }
  }
  const currentYear = new Date().getFullYear();
  const endCumulYear =
    indicator.dateCumulYear !== null &&
      indicator.dateCumulYear !== undefined &&
      indicator.dateCumulYear !== ""
      ? Number(indicator.dateCumulYear)
      : null;

  const shouldCumulateForYear = (
    ind: {
      cumulative?: boolean;
      cumulativeProgram?: boolean;
      cumulativeDashboard?: boolean;
      cumulativeReporting?: boolean;
      cumulativeYear?: boolean;
      dateCumulYear?: number | string | null;
    },
    targetYear: number,
    currentContext?: "dashboard" | "project" | "programme" | null
  ): boolean => {
    const cumulOptions = getIndicatorCumulativeOption(ind, currentContext);

    if (!cumulOptions) {
      return false;
    }

    if (cumulOptions && !ind.cumulativeYear) {
      return true;
    }

    const endCumulYear =
      ind.dateCumulYear !== null &&
        ind.dateCumulYear !== undefined &&
        ind.dateCumulYear !== ""
        ? Number(ind.dateCumulYear)
        : null;

    if (!endCumulYear || Number.isNaN(endCumulYear)) {
      return true;
    }

    if (endCumulYear > currentYear) {
      return targetYear <= endCumulYear;
    } else {
      return targetYear <= endCumulYear;
    }
  };
  const chartDataYear = arrYear.map((_, k: number) => ({
    legend: (launchDate + k).toString(),
    prevu: 0,
    realise: 0,
    verifie: 0,
  }));

  // Étape 1 : Regrouper par nom d'indicateur, puis par année
  const groupedByNameAndYear = reportingIndicatorValues.reduce(
    (
      acc: Record<
        string,
        Record<
          string,
          {
            compte: Record<number, number>;
            [key: number]: number;
          }
        >
      >,
      indicator
    ) => {
      const name = indicator.indicator.name;
      const year = indicator.year
        ? indicator.year.toString()
        : new Date().getFullYear().toString();
      const month = indicator.month ?? 0;
      const type = indicator.type;
      const moyenne = indicator.indicator.moyenne;
      const cumulOptions = Boolean(context == "dashboard" ? indicator.indicator.cumulativeDashboard : context == "programme" ? indicator.indicator.cumulativeProgram : indicator.indicator.cumulativeReporting);

      // Initialiser l'indicateur
      if (!acc[name])
        acc[name] = {
          moyenne: moyenne,
        };

      // Initialiser l'année (avec cumul si nécessaire)
      if (!acc[name][year]) {
        let compteInitial = { 1: 0, 2: 0, 3: 0 };
        const yearNumber = Number(year);
        const isCumulative = isGlobalContext
          ? Boolean(cumulOptions) &&
          (!indicator.indicator.cumulativeYear ||
            !endCumulYear ||
            yearNumber <= endCumulYear)
          : shouldCumulateForYear(indicator.indicator, yearNumber, context);
        if (
          isCumulative &&
          indicator.indicator.cumulativeYear &&
          year !== launchDate.toString()
        ) {
          const previousYear = (parseInt(year) - 1).toString();
          if (acc[name][previousYear]?.compte) {
            compteInitial = {
              1: acc[name][previousYear].compte[1],
              2: acc[name][previousYear].compte[2],
              3: acc[name][previousYear].compte[3],
            };
          }
        }

        acc[name][year] = {
          compte: compteInitial,
        };
      }

      const isFutureActual = parseInt(year) > currentYear && type !== 1;
      const isCumulativeIndicator = Boolean(context == "dashboard" ? indicator.indicator.cumulativeDashboard : context == "programme" ? indicator.indicator.cumulativeProgram : indicator.indicator.cumulativeReporting);

      if (isFutureActual) {
        acc[name][year][type] = 0;
      } else if (indicator.indicator.code === "tco2_sequestrees") {
        acc[name][year][type] = indicator.yearValue ?? 0;
      } else if (isCumulativeIndicator) {
        acc[name][year][type] = indicator.yearValue ?? indicator.value ?? 0;
      } else {
        if (
          indicator.yearValue !== undefined &&
          indicator.yearValue !== null &&
          indicator.yearValue !== 0
        ) {
          if (parseInt(year) > currentYear && type !== 1) {
            acc[name][year][type] = 0;
          } else if (
            isGlobalContext &&
            cumulOptions &&
            indicator.indicator.cumulativeYear &&
            endCumulYear &&
            Number(year) > endCumulYear
          ) {
            acc[name][year][type] = Math.max(
              acc[name][year][type] ?? 0,
              indicator.yearValue
            );
          } else {
            acc[name][year][type] = indicator.yearValue;
          }
        } else {
          acc[name][year][type] =
            (acc[name][year][type] ?? 0) + indicator.value;


        }
      }


      // 🧮 INCRÉMENT DU COMPTE (0 inclus)
      if (
        indicator.valuewithoutCumul !== null &&
        indicator.valuewithoutCumul !== undefined
      ) {
        acc[name][year].compte[type] += Number((indicator as any).count ?? 1);;
      }

      return acc;
    },
    {}
  );
  // console.log("groupedByNameAndYear", groupedByNameAndYear);
  if (indicator.id == 7) {
    console.log("groupedByNameAndYear", groupedByNameAndYear);
    console.log("cd", reportingIndicatorValues.filter(
      (item) =>
        item.year === 2028 &&
        item.indicator.id === 7
    ));
  }

  // Étape 2 : Mettre à jour les données du graphique
  if (groupedByNameAndYear[indicator.name] != undefined) {
    let lastPrevuValue = 0;
    let lastRealiseValue = 0;
    let lastVerifieValue = 0;

    chartDataYear.forEach((yearData) => {
      const year = yearData.legend; // Année actuelle de chartDataYear
      const yearNumber = Number(year);
      const prevu = groupedByNameAndYear?.[indicator.name]?.[year]?.[1];
      const cumulOptions = context == "dashboard" ? indicator.cumulativeDashboard : context == "programme" ? indicator.cumulativeProgram : indicator.cumulativeReporting;
      const count1 =
        groupedByNameAndYear?.[indicator.name]?.[year]?.["compte"]?.[1];
      const shouldCumulateThisYear = shouldCumulateForYear(
        indicator,
        yearNumber,
        context
      );
      const isAfterCumulEnd =
        indicator.cumulativeYear === true   // est cumulatif par année
        && cumulOptions   // mais pas cumulatif global
        && endCumulYear                   // une fin de cumul est définie
        && Number(year) > endCumulYear;    // et on dépasse cette année  
      const shouldAddPreviousYear =
        cumulOptions &&
        shouldCumulateThisYear &&
        !isAfterCumulEnd;
      const shouldAddPreviousYearForActuals =
        shouldAddPreviousYear && yearNumber <= currentYear;

      if (prevu !== undefined) {
        let yearlyPrevu = isNaN(prevu) ? 0 : prevu;
        if (shouldAddPreviousYear) {
          yearlyPrevu += lastPrevuValue ?? 0;
        }
        yearData.prevu = yearlyPrevu;
        if (groupedByNameAndYear?.[indicator.name]?.["moyenne"]) {
          const dataTemp1 = yearlyPrevu;
          yearData.prevu = dataTemp1 / (count1 != 0 ? count1 : 1);
        }
      } else if (
        cumulOptions &&
        shouldCumulateThisYear
      ) {
        yearData.prevu = lastPrevuValue;
      }
      lastPrevuValue = yearData.prevu;
      const realise = groupedByNameAndYear?.[indicator.name]?.[year]?.[2];
      const count2 =
        groupedByNameAndYear?.[indicator.name]?.[year]?.["compte"]?.[2];

      if (realise !== undefined) {
        let yearlyRealise = isNaN(realise) ? 0 : realise;
        if (shouldAddPreviousYearForActuals) {
          yearlyRealise += lastRealiseValue ?? 0;
        }
        yearData.realise = yearlyRealise;
        if (groupedByNameAndYear?.[indicator.name]?.["moyenne"]) {
          const dataTemp2 = yearlyRealise;
          yearData.realise = dataTemp2 / (count2 != 0 ? count2 : 1);
        }
      } else if (
        cumulOptions &&
        shouldCumulateThisYear &&
        yearNumber <= currentYear
      ) {
        yearData.realise = lastRealiseValue;
      }
      lastRealiseValue = yearData.realise;
      const verifie = groupedByNameAndYear?.[indicator.name]?.[year]?.[3];
      const count3 =
        groupedByNameAndYear?.[indicator.name]?.[year]?.["compte"]?.[3];

      if (verifie !== undefined) {
        let yearlyVerifie = isNaN(verifie) ? 0 : verifie;
        if (shouldAddPreviousYearForActuals) {
          yearlyVerifie += lastVerifieValue ?? 0;
        }
        yearData.verifie = yearlyVerifie;
        if (groupedByNameAndYear?.[indicator.name]?.["moyenne"]) {
          const dataTemp3 = yearlyVerifie;
          yearData.verifie = dataTemp3 / (count3 != 0 ? count3 : 1);
        }
      } else if (
        cumulOptions &&
        shouldCumulateThisYear &&
        yearNumber <= currentYear
      ) {
        yearData.verifie = lastVerifieValue;
      }
      lastVerifieValue = yearData.verifie;
    });
  }
  return chartDataYear;
}

export function getChartDataTrimestre(
  reportingIndicatorValues: {
    id: number;
    indicator: any; // garde "any" si c'est ton typage actuel
    year: number;
    month: number; // 0 = janv … 11 = déc (comme dans getChartDataMonth)
    type: number; // 1=prévu, 2=réalisé, 3=vérifié
    value: number;
  }[],
  expectedDurationYear: number,
  indicator: Indicator,
  launchDate: number,
  context?: "dashboard" | "project" | "programme" | "global" | null
) {
  const getQuarterCount = (row: { compte?: number[] }, idx: number) => {
    if (!Array.isArray(row?.compte)) {
      return 1;
    }

    const count = Number(row.compte[idx] ?? 0);
    return Number.isFinite(count) && count > 0 ? count : 1;
  };
  // On s'aligne sur getChartDataYear : années = launchDate + index
  const arrYear = generateYearArray(expectedDurationYear);

  const result: {
    legend: string;
    prevu: number;
    realise: number;
    verifie: number;
  }[] = [];

  // Renvoie la dernière valeur définie pour un champ dans [startIdx..endIdx] (sans slice)
  function getLastOfQuarter(
    data: {
      prevu: number;
      realise: number;
      verifie: number;
      moyenne: boolean;
      cummulative: boolean;
      valueWithoutCumul: number[];
    }[],
    startIdx: number,
    endIdx: number
  ) {
    // on part de la fin du trimestre, puis on remonte si nécessaire
    let cumulvalue = [0, 0, 0];
    let countablevalue = [0, 0, 0];
    let moyenne = false;

    for (let i = endIdx; i >= startIdx; i--) {
      const row = data[i];
      moyenne = moyenne || row.moyenne;

      if (row.cummulative) {
        if (!row) continue;
        const hasAny =
          row.prevu !== undefined ||
          row.realise !== undefined ||
          row.verifie !== undefined;
        if (hasAny) {
          return {
            prevu: Number(row.prevu ?? 0),
            realise: Number(row.realise ?? 0),
            verifie: Number(row.verifie ?? 0),
          };
        }
      } else {
        row.valueWithoutCumul.forEach((val, idx) => {
          if (val !== null && val !== undefined) {
            countablevalue[idx] += getQuarterCount(row as any, idx);
          }
        });
        cumulvalue[0] += row.prevu;
        cumulvalue[1] += row.realise;
        cumulvalue[2] += row.verifie;
      }
    }

    if (cumulvalue[0] !== 0 || cumulvalue[1] !== 0 || cumulvalue[2] !== 0) {
      return {
        prevu: moyenne
          ? cumulvalue[0] / (countablevalue[0] || 1)
          : cumulvalue[0],
        realise: moyenne
          ? cumulvalue[1] / (countablevalue[1] || 1)
          : cumulvalue[1],
        verifie: moyenne
          ? cumulvalue[2] / (countablevalue[2] || 1)
          : cumulvalue[2],
      };
    }
    // si rien trouvé, renvoyer 0
    return { prevu: 0, realise: 0, verifie: 0 };
  }

  arrYear.forEach((_, idx) => {
    const year = launchDate + idx;

    // 1) Série mensuelle (12 items: Jan..Déc) déjà conforme à tes règles
    const monthData = getChartDataMonth(
      reportingIndicatorValues as any,
      year,
      indicator as any,
      context ?? "project"
    ); // [{legend, prevu, realise, verifie}] x12 (index 0..11)
    // console.log("monthData for year", year, monthData);
    // 2) Définition des bornes de trimestres (indices 0-based)
    //    T1: 0..2 (Jan–Mar), T2: 3..5, T3: 6..8, T4: 9..11
    const quarters = [
      { legend: `T1 ${year}`, start: 0, end: 2 },
      { legend: `T2 ${year}`, start: 3, end: 5 },
      { legend: `T3 ${year}`, start: 6, end: 8 },
      { legend: `T4 ${year}`, start: 9, end: 11 },
    ].map(({ legend, start, end }) => {
      const last = getLastOfQuarter(monthData, start, end);

      return {
        legend,
        prevu: last.prevu,
        realise: last.realise,
        verifie: last.verifie,
      };
    });

    result.push(...quarters);
  });

  return result;
}
export function animatePercentage(
  targetPercentage: number,
  setPercentage: (value: number) => void,
  duration: number = 2000 // Durée de l'animation en ms
) {
  const start = performance.now();

  function step(currentTime: number) {
    const elapsed = currentTime - start;
    const progress = Math.min(elapsed / duration, 1); // Limiter à 1 (100%)
    const value = Math.floor(progress * targetPercentage);

    setPercentage(value);

    if (progress < 1) {
      requestAnimationFrame(step); // Continuer l'animation
    }
  }

  requestAnimationFrame(step); // Démarrer l'animation
}

/**
 * Génère un tableau de nombres séquentiels représentant les années.
 *
 * @param duration - La durée en années pour générer le tableau.
 * @returns Un tableau de nombres allant de 1 à duration.
 */
export const generateYearArray = (duration: number): number[] => {
  return Array.from({ length: duration }, (_, index) => index + 1);
};

export const calculateExpectedDurationYear = (
  dateWithAddedMonths: Date | null,
  launchDate: number | null
): number => {
  if (!dateWithAddedMonths || !launchDate) return 0;

  const addedYear = dateWithAddedMonths.getFullYear();
  return addedYear === launchDate ? 1 : addedYear - (launchDate - 1);
};

export const convertArea = (
  m2: number,
  unit: string,
  decimals: number = 2
): number => {
  let result: number;

  switch (unit) {
    case "ha":
      result = m2 / 10000;
      break;
    case "m2":
      result = m2;
      break;
    case "km2":
      result = m2 / 1_000_000;
      break;
    case "square_nautical_miles":
      // 1 square nautical mile = 3,429,900 m²
      result = m2 / 3_429_900;
      break;
    default:
      result = m2; // Par défaut, retour en m² si l'unité n'est pas reconnue
  }

  return parseFloat(result.toFixed(decimals)); // Ajuste le nombre de décimales
};

/**
 * Fonction pour traiter les données des parcelles et retourner une liste de données structurées.
 * Cette fonction parcourt tous les éléments de la référence `parcelsItemsRef`,
 * filtre les couches qui sont des polygones Leaflet (`L.Polygon`) et qui contiennent des données personnalisées (`customData`),
 * puis les traite selon qu'elles proviennent de la base de données ou non.
 *
 * @returns {Array} - Un tableau contenant les données traitées des polygones. Chaque élément est une représentation structurée des données d'une parcelle.
 *
 */
export function getProcessedParcelData(parcelsItemsRef) {
  const polygons = [];

  parcelsItemsRef.current.eachLayer((layer) => {
    if (
      layer instanceof L.Polygon && // Vérifie si la couche est un polygone.
      layer.customData != undefined && // Vérifie que customData est défini.
      layer.customData != null // Vérifie que customData n'est pas null.
    ) {
      // Traite les données différemment selon leur origine.
      if (layer.inBdd == false) {
        // Les données ne proviennent pas de la base de données.
        layer.customData.geometry.coordinates =
          layer.customData.geometry.coordinates[0][0];
        if (layer.geometry) {
          delete layer.geometry;
        }
        const processedData = dataProcessingParcel(layer.customData, "0", true);
        polygons.push(processedData);
      } else {
        // Les données proviennent de la base de données.
        if (layer.geometry) {
          delete layer.geometry;
        }
        polygons.push({
          ...layer.customData,
          contenance: parseInt(layer.customData.contenance), // Convertit `contenance` en entier.
          feuille: parseInt(layer.customData.feuille), // Convertit `feuille` en entier.
        });
      }
    }
  });

  // Filtrage des doublons par sérialisation des objets
  const uniquePolygons = polygons.filter(
    (polygon, index, arr) =>
      arr.findIndex(
        (item) => JSON.stringify(item) === JSON.stringify(polygon)
      ) === index
  );

  return uniquePolygons; // Retourne le tableau des données traitées sans doublons.
}

/**
 * Fonction pour extraire et transformer les données des polygones dessinés sur une carte Leaflet.
 * Cette fonction parcourt tous les éléments de la référence `drawItemsRef`,
 * filtre les couches qui sont des polygones (`L.Polygon`),
 * récupère leurs coordonnées géographiques (latitude et longitude),
 * puis retourne une version transformée des coordonnées.
 *
 * @returns {Array} - Un tableau contenant les coordonnées transformées des polygones.
 * Chaque élément du tableau est un tableau de coordonnées [latitude, longitude].
 *
 * @example
 * const processedLocations = getProcessedLocationData();
 * console.log(processedLocations);
 * // Exemple de résultat : [ [[48.8566, 2.3522], [48.8570, 2.3530], ...], ... ]
 */
export function getProcessedLocationData(drawItemsRef) {
  const polygons = [];

  // Parcourt toutes les couches pour identifier les polygones.
  drawItemsRef.current.eachLayer((layer) => {
    if (layer instanceof L.Polygon) {
      // Vérifie que la couche est un polygone.
      const latLngs = layer.getLatLngs(); // Récupère les coordonnées (brutes).
      polygons.push(latLngs); // Ajoute les coordonnées au tableau `polygons`.
    }
  });

  // Transforme les coordonnées brutes en un tableau de [latitude, longitude].
  const processedPolygons = polygons.map((polygon) =>
    polygon[0].map((latLng) => [latLng.lat, latLng.lng])
  );

  // Supprime les doublons en comparant les coordonnées des polygones.
  const uniquePolygons = processedPolygons.filter(
    (polygon, index, arr) =>
      arr.findIndex(
        (item) => JSON.stringify(item) === JSON.stringify(polygon)
      ) === index
  );

  return uniquePolygons;
}

export function getPaddingForZoom(zoom) {
  if (zoom < 8) return 120;
  if (zoom < 9) return 80;
  if (zoom < 10) return 65;
  if (zoom < 11) return 50;
  if (zoom < 13) return 40;
  if (zoom < 14) return 30;
  if (zoom < 16) return 20;
  if (zoom < 18) return 10;
  return 5;
}

export const monthToString = (value) => {
  const months = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];

  return months[value];
};

const getDatePeriod = (startYear, endYear) => {
  const year = [];
  for (let i = startYear; i <= endYear; i++) {
    for (let j = 0; j < 12; j++) {
      year.push({
        month: j,
        year: i,
      });
    }
  }

  return year;
};

export const calculReporting = (data, type = null, projectSelected = []) => {
  const currentYear = new Date().getFullYear();
  const minDate = parseInt(data.minDate);
  const maxDate = parseInt(data.maxDate);
  const monthsOther = getDatePeriod(minDate, maxDate);
  const months = monthsOther;
  // return;
  const types = [1, 2, 3];
  const projectIndicators = [];
  const projectTotalIndicators = [];
  const hasData = [];
  const projects = [];
  let maxTco2Year = 0;
  data?.programs?.forEach((program) => {
    program?.projects?.forEach((project) => {
      if (
        (type && project.type.type !== type) ||
        (projectSelected?.length &&
          !projectSelected?.find((ps) => ps.id === project.id))
      )
        return;
      projects.push(project);
      let tco2Year = null;
      data?.parameter?.default?.forEach((p) => {
        if (p.tco2_year !== undefined && p.tco2_year !== null) {
          tco2Year = p.tco2_year;
        }
      });
      data?.parameter?.custom?.forEach((p) => {
        if (
          p.tco2_year !== undefined &&
          p.tco2_year !== null &&
          p.project == project.id
        ) {
          tco2Year = p.tco2_year;
        }
      });

      if (tco2Year !== null && tco2Year > maxTco2Year) {
        maxTco2Year = parseInt(tco2Year);
      }
    });
  });

  const monthsTco2 = getDatePeriod(minDate, minDate + maxTco2Year);

  types.forEach((type) => {
    const totalArbres = [];
    for (let keyi = 0; keyi < data?.indicators?.length; keyi++) {
      const indicator = data?.indicators[keyi];

      projectTotalIndicators.push({
        indicator: indicator,
        type: type,
        total: 0,
        totalYear: maxDate - minDate + 1,
      });

      if (
        "kg_co2_arbre_an" !== indicator?.code &&
        "tonne_co2_ha_an" !== indicator?.code &&
        "type-d-arrosage" !== indicator?.code &&
        (indicator?.projectOwnerReporting !== null ||
          indicator?.code === "arbres_plantes")
      ) {
        let lastDefaultValue = 0;
        let lastValue = 0;
        let totalYear = 0;
        let allTotalYear = 0;
        let lastLoopYear = 0;
        const alreadyAdd = [];
        if (indicator?.code !== "tco2_sequestrees") {
          /*if (indicator.code === "arbres_plantes") {
            months = monthsTco2;
          } else {
            months = monthsOther;
          }*/
          for (let key = 0; key < months.length; key++) {
            const month = months[key];
            if (type != 1 && month.year > currentYear) {
              continue;
            }

            projects?.forEach((project) => {
              project?.indicators.forEach((pi: any) => {
                if (pi.code === indicator.code) {
                  const arrayHas = project?.projectIndicators.filter(
                    (projectIndicator: any) =>
                      projectIndicator?.month == month.month &&
                      projectIndicator?.year == month?.year &&
                      projectIndicator?.indicator?.id == indicator?.id &&
                      projectIndicator?.type == type
                  );

                  if (!indicator?.cumulative && month?.year != lastLoopYear) {
                    totalYear = 0;
                  }

                  // estimated
                  if (type == 1) {
                    let defaultValue = 0;
                    // const values = [];
                    const values =
                      project?.projectIndicatorDefaultValues?.filter(
                        (dv) => dv.indicator === indicator
                      );

                    if (values?.length > 0) {
                      defaultValue = values[0].value;

                      if (defaultValue && arrayHas.length == 0) {
                        const frequency = values.frequency;

                        if (0 === lastDefaultValue || indicator.cumulative) {
                          lastDefaultValue = defaultValue;
                          totalYear = defaultValue;
                        } else {
                          switch (frequency) {
                            case 2:
                              if (0 == key % 3) {
                                lastDefaultValue += defaultValue;
                                totalYear += defaultValue;
                              }
                              break;
                            case 3:
                              if (0 == key % 12) {
                                lastDefaultValue += defaultValue;
                                totalYear += defaultValue;
                              }
                              break;
                            default:
                              lastDefaultValue += defaultValue;
                              totalYear += defaultValue;
                              break;
                          }
                        }
                      }

                      if (arrayHas.length > 0) {
                        const firstElement = arrayHas[0];
                        lastDefaultValue += firstElement.value;
                        totalYear += firstElement.value;
                        allTotalYear += firstElement.value;

                        if (
                          !indicator.cumulative &&
                          month.year != lastLoopYear
                        ) {
                          totalYear = 0;
                        }

                        projectIndicators.push({
                          id: 0,
                          indicator: indicator,
                          month: month.month,
                          year: month.year,
                          type: type,
                          value: indicator.cumulative
                            ? lastDefaultValue
                            : firstElement.value,
                          yearValue: totalYear,
                        });

                        if (
                          !projectTotalIndicators.find(
                            (pti) =>
                              pti.indicator.id === indicator.id &&
                              pti.type === type
                          )
                        ) {
                          projectTotalIndicators.push({
                            indicator: indicator,
                            type: type,
                            total: 0,
                            totalYear: maxDate - minDate + 1,
                          });
                        }

                        projectTotalIndicators.map((pti) => {
                          if (
                            pti.indicator.id === indicator.id &&
                            pti.type === type
                          ) {
                            pti.total += firstElement.value;
                          }
                          return pti;
                        });

                        if (!hasData.includes(indicator.id)) {
                          hasData.push(indicator.id);
                        }

                        if ("arbres_plantes" === indicator.code) {
                          if (totalArbres[month.year] === undefined) {
                            totalArbres[month.year] = 0;
                          }
                          totalArbres[month.year] += firstElement.value;
                        }
                      } else {
                        const valueToUse =
                          defaultValue !== 0 ? defaultValue : 0;

                        lastDefaultValue += valueToUse;
                        totalYear += valueToUse;
                        allTotalYear += valueToUse;

                        projectIndicators.push({
                          id: 0,
                          indicator: indicator,
                          month: month.month,
                          year: month.year,
                          type: type,
                          value: indicator.cumulative
                            ? lastDefaultValue
                            : valueToUse,
                          yearValue: totalYear,
                          maxDate:
                            indicator.code === "arbres_plantes"
                              ? minDate + maxTco2Year
                              : null,
                        });

                        if (!hasData.includes(indicator.id)) {
                          hasData.push(indicator.id);
                        }

                        if (
                          !projectTotalIndicators.find(
                            (pti) =>
                              pti.indicator.id === indicator.id &&
                              pti.type === type
                          )
                        ) {
                          projectTotalIndicators.push({
                            indicator: indicator,
                            type: type,
                            total: 0,
                            totalYear: maxDate - minDate + 1,
                          });
                        }

                        projectTotalIndicators.map((pti) => {
                          if (
                            pti.indicator.id === indicator.id &&
                            pti.type === type &&
                            type === 1
                          ) {
                            pti.total += valueToUse;
                          }
                          return pti;
                        });

                        if (
                          !indicator.cumulative &&
                          month.year != lastLoopYear
                        ) {
                          totalYear = 0;
                        }

                        if ("arbres_plantes" === indicator.code) {
                          if (totalArbres[month.year] === undefined) {
                            totalArbres[month.year] = 0;
                          }
                          totalArbres[month.year] += valueToUse;
                        }
                      }
                    } else if (arrayHas.length > 0) {
                      const firstElement = arrayHas[0];
                      lastDefaultValue += firstElement.value;
                      totalYear = lastDefaultValue;

                      if (!indicator.cumulative && month.year != lastLoopYear) {
                        totalYear = 0;
                      }

                      projectIndicators.push({
                        id: 0,
                        indicator: indicator,
                        month: month.month,
                        year: month.year,
                        type: type,
                        value: indicator.cumulative
                          ? lastDefaultValue
                          : firstElement.value,
                        yearValue: totalYear,
                        maxDate:
                          indicator.code === "arbres_plantes"
                            ? minDate + maxTco2Year
                            : null,
                      });

                      if (
                        !projectTotalIndicators.find(
                          (pti) =>
                            pti.indicator.id === indicator.id &&
                            pti.type === type
                        )
                      ) {
                        projectTotalIndicators.push({
                          indicator: indicator,
                          type: type,
                          total: 0,
                          totalYear: maxDate - minDate + 1,
                          maxDate:
                            indicator.code === "arbres_plantes"
                              ? minDate + maxTco2Year
                              : null,
                        });
                      }

                      projectTotalIndicators.map((pti) => {
                        if (
                          pti.indicator.id === indicator.id &&
                          pti.type === type
                        ) {
                          pti.total += firstElement.value;
                        }
                        return pti;
                      });

                      if (!hasData.includes(indicator.id)) {
                        hasData.push(indicator.id);
                      }

                      if ("arbres_plantes" === indicator.code) {
                        if (totalArbres[month.year] === undefined) {
                          totalArbres[month.year] = 0;
                        }
                        totalArbres[month.year] += firstElement.value;
                      }
                    } else {
                      projectIndicators.push({
                        id: 0,
                        indicator: indicator,
                        month: month.month,
                        year: month.year,
                        type: type,
                        value: lastDefaultValue,
                        yearValue: totalYear,
                        maxDate:
                          indicator.code === "arbres_plantes"
                            ? minDate + maxTco2Year
                            : null,
                      });
                    }
                  } else {
                    let v = 0;
                    if (arrayHas.length > 0) {
                      const firstElement = arrayHas[0];
                      v = firstElement.value;
                    }

                    lastValue += v;

                    totalYear = lastValue;

                    if (!indicator.cumulative && month.year != lastLoopYear) {
                      totalYear = 0;
                    }

                    projectIndicators.push({
                      id: 0,
                      indicator: indicator,
                      month: month.month,
                      year: month.year,
                      type: type,
                      value: indicator.cumulative ? lastValue : v,
                      yearValue: totalYear,
                      maxDate:
                        indicator.code === "arbres_plantes"
                          ? minDate + maxTco2Year
                          : null,
                    });

                    if (
                      !projectTotalIndicators.find(
                        (pti) =>
                          pti.indicator.id === indicator.id && pti.type === type
                      )
                    ) {
                      projectTotalIndicators.push({
                        indicator: indicator,
                        type: type,
                        total: 0,
                        totalYear: maxDate - minDate + 1,
                      });
                    }

                    projectTotalIndicators.map((pti) => {
                      if (
                        pti.indicator.id === indicator.id &&
                        pti.type === type
                      ) {
                        pti.total += v;
                      }
                      return pti;
                    });

                    if (!hasData.includes(indicator.id) && lastValue > 0) {
                      hasData.push(indicator.id);
                    }

                    if ("arbres_plantes" === indicator.code) {
                      if (totalArbres[month.year] === undefined) {
                        totalArbres[month.year] = 0;
                      }
                      totalArbres[month.year] += v;
                    }
                  }
                }
              });
            });

            lastLoopYear = month.year;
          }
        } else {
          for (let key = 0; key < monthsTco2.length; key++) {
            const month = monthsTco2[key];
            if (type != 1 && month.year > currentYear) {
              continue;
            }
            projects.forEach((project) => {
              let tco2Unit = null;
              let tco2Year = null;
              data.parameter.default.forEach((p) => {
                if (p.tco2_unit !== undefined && p.tco2_unit !== null) {
                  tco2Unit = p.tco2_unit;
                }
                if (p.tco2_year !== undefined && p.tco2_year !== null) {
                  tco2Year = p.tco2_year;
                }
              });
              data.parameter.custom.forEach((p) => {
                if (
                  p.tco2_unit !== undefined &&
                  p.tco2_unit !== null &&
                  p.project == project.id
                ) {
                  tco2Unit = p.tco2_unit;
                }
                if (
                  p.tco2_year !== undefined &&
                  p.tco2_year !== null &&
                  p.project == project.id
                ) {
                  tco2Year = p.tco2_year;
                }
              });

              let t = 0;
              totalArbres.forEach((v, y) => {
                if (y <= month.year) {
                  t += v;
                }
              });

              if (!alreadyAdd.find((ad) => ad == month.year)) {
                totalYear += tco2Unit * t;
                alreadyAdd.push(month.year);
              }

              projectIndicators.push({
                id: 0,
                indicator: indicator,
                month: month.month,
                year: month.year,
                type: type,
                value: totalYear ? totalYear / 12 : 0,
                yearValue: totalYear ? totalYear : 0,
                unitValue: tco2Unit,
                totalArbres: totalArbres,
                t: t,
                maxDate: minDate + maxTco2Year,
              });

              if (
                t !== 0 &&
                !projectTotalIndicators.find(
                  (pti) =>
                    pti.indicator.id === indicator.id && pti.type === type
                )
              ) {
                projectTotalIndicators.push({
                  indicator: indicator,
                  type: type,
                  total: 0,
                  totalYear: maxTco2Year,
                });
              }

              projectTotalIndicators.map((pti) => {
                if (pti.indicator.id === indicator.id && pti.type === type) {
                  pti.total = totalYear ? Math.round(totalYear) : 0;
                }
                return pti;
              });

              if (!hasData.includes(indicator.id) && totalYear) {
                hasData.push(indicator.id);
              }
            });
          }
        }
      }
    }
  });

  return { projectIndicators, projectTotalIndicators, hasData, maxTco2Year };
};

export const calculateFundingTotals = async (projects) => {
  const desired = await Promise.all(
    projects
      .flatMap((p) => p.desiredFunding || [])
      .map((f) => convertToEuro(f.amount, f.devise))
  );

  const obtained = await Promise.all(
    projects
      .flatMap((p) => p.obtainedFunding || [])
      .map((f) => convertToEuro(f.amount, f.devise))
  );

  const totalDesired = desired.reduce((acc, v) => acc + v, 0);
  const totalObtained = obtained.reduce((acc, v) => acc + v, 0);
  const fees = totalDesired * 0.15;
  const callContributions = totalDesired + fees;

  return {
    totalDesired,
    totalObtained,
    fees,
    callContributions,
  };
};

// export const calculReportingProject = (
//   data: any,
//   type: number | null = null,
//   projectSelected: any[] = [],
//   isForProject = false
// ) => {
//   let minDate: number | null = null;
//   let maxDate: number | null = null;
//   let startMonth = 0;
//   let endMonth = 11;

//   function getDatePeriodByProjectInfo(
//     startYear: number,
//     endYear: number,
//     startMonth = 0,
//     endMonth = 11
//   ) {
//     const months: { year: number; month: number }[] = [];
//     for (let year = startYear; year <= endYear; year++) {
//       const monthStart = year === startYear ? startMonth : 0;
//       const monthEnd = year === endYear ? endMonth : 11;
//       for (let month = monthStart; month <= monthEnd; month++) {
//         months.push({ year, month });
//       }
//     }
//     return months;
//   }

//   const ymToIndex = (y: number, m: number) => y * 12 + m;

//   function totArbres(arbres: any[]) {
//     return arbres.reduce((acc, a) => acc + a, 0);
//   }

//   function isWithinProjectRange(
//     y: number,
//     m: number,
//     project: {
//       launchDate?: string | Date | null;
//       expectedDuration?: number | null;
//       endDate?: string | Date | null;
//     },
//     tco2Year: number = 0
//   ): boolean {
//     if (!project?.launchDate) return true;

//     const launch = new Date(project.launchDate);
//     if (Number.isNaN(launch.getTime())) return true;

//     const minIndex = ymToIndex(launch.getFullYear(), launch.getMonth());
//     const curIndex = ymToIndex(y, m);

//     const duration = tco2Year
//       ? (tco2Year - 1) * 12
//       : Number(project.expectedDuration ?? 0);

//     if (!duration || duration <= 0) {
//       return curIndex >= minIndex;
//     }

//     const maxIndex = minIndex + (duration - 1);
//     return curIndex >= minIndex && curIndex <= maxIndex;
//   }

//   // ➕ Date d'aujourd'hui (année/mois) + helper futur
//   const now = new Date();
//   const nowY = now.getFullYear();
//   const nowM = now.getMonth();
//   const isFutureMonth = (y: number, m: number) =>
//     y > nowY || (y === nowY && m > nowM);

//   if (isForProject === true) {
//     const project = projectSelected?.[0];

//     if (project?.launchDate && project?.expectedDuration) {
//       const startDate = new Date(project.launchDate);
//       const monthsToAdd = Number(project.expectedDuration);

//       const endDate = new Date(startDate);
//       endDate.setMonth(endDate.getMonth() + monthsToAdd);
//       endDate.setDate(0);

//       minDate = startDate.getFullYear();
//       maxDate = endDate.getFullYear();
//       startMonth = startDate.getMonth();
//       endMonth = endDate.getMonth();
//     } else if (project?.launchDate && project?.endDate) {
//       const startDate = new Date(project.launchDate);
//       const endDate = new Date(project.endDate);

//       minDate = startDate.getFullYear();
//       maxDate = endDate.getFullYear();
//       startMonth = startDate.getMonth();
//       endMonth = endDate.getMonth();
//     }
//   } else {
//     (data?.programs ?? []).forEach((program: any) => {
//       (program?.projects ?? []).forEach((project: any) => {
//         const launchDate = project?.launchDate
//           ? new Date(project.launchDate)
//           : null;
//         if (launchDate) {
//           const ly = launchDate.getFullYear();
//           const lm = launchDate.getMonth();
//           if (
//             minDate === null ||
//             ly < (minDate as number) ||
//             (ly === minDate && lm < startMonth)
//           ) {
//             minDate = ly;
//             startMonth = lm;
//           }
//         }

//         let projectEndDate: Date | null = null;
//         if (project?.expectedDuration && launchDate) {
//           const ed = new Date(launchDate);
//           ed.setMonth(ed.getMonth() + Number(project.expectedDuration));
//           ed.setDate(0);
//           projectEndDate = ed;
//         } else if (project?.endDate) {
//           projectEndDate = new Date(project.endDate);
//         }

//         if (projectEndDate) {
//           const ey = projectEndDate.getFullYear();
//           const em = projectEndDate.getMonth();
//           if (
//             maxDate === null ||
//             ey > (maxDate as number) ||
//             (ey === maxDate && em > endMonth)
//           ) {
//             maxDate = ey;
//             endMonth = em;
//           }
//         }
//       });
//     });
//   }

//   const monthsOther = getDatePeriodByProjectInfo(
//     minDate as number,
//     maxDate as number,
//     startMonth,
//     endMonth
//   );
//   const months = monthsOther;
//   const types = [1, 2, 3];
//   const projectIndicators: any[] = [];
//   const projectTotalIndicators: any[] = [];
//   const hasData: number[] = [];
//   const projects: any[] = [];

//   const currentYear = nowY; // (gardé pour logs éventuels)
//   let maxTco2Year = 0;

//   data.programs.forEach((program: any) => {
//     program.projects.forEach((project: any) => {
//       if (
//         projectSelected.length &&
//         !projectSelected.find((ps: any) => ps.id === project.id)
//       )
//         return;

//       projects.push(project);

//       let tco2Year: number | null = null;
//       data.parameter.default.forEach((p: any) => {
//         if (p.tco2_year !== undefined && p.tco2_year !== null) {
//           tco2Year = parseInt(p.tco2_year);
//         }
//       });

//       data.parameter.custom.forEach((p: any) => {
//         if (
//           p.tco2_year !== undefined &&
//           p.tco2_year !== null &&
//           p.project == project.id
//         ) {
//           tco2Year = parseInt(p.tco2_year);
//         }
//       });

//       if (tco2Year !== null && tco2Year > maxTco2Year) {
//         maxTco2Year = parseInt(String(tco2Year), 10);
//       }
//     });
//   });

//   const monthsTco2 = getDatePeriodByProjectInfo(
//     minDate as number,
//     (minDate as number) + maxTco2Year,
//     startMonth,
//     endMonth
//   );

//   types.forEach((type) => {
//     const totalArbres: number[][] = [];
//     for (let keyi = 0; keyi < data.indicators.length; keyi++) {
//       const indicator = data.indicators[keyi];

//       projectTotalIndicators.push({
//         indicator: indicator,
//         type: type,
//         total: 0,
//         totalYear: (maxDate as number) - (minDate as number) + 1,
//       });

//       if (
//         "kg_co2_arbre_an" !== indicator.code &&
//         "tonne_co2_ha_an" !== indicator.code &&
//         "type-d-arrosage" !== indicator.code
//       ) {
//         let lastValue = 0;
//         let totalYear = 0;
//         let lastLoopYear = 0;
//         const alreadyAdd: number[] = [];

//         if (indicator.code !== "tco2_sequestrees") {
//           for (let key = 0; key < months.length; key++) {
//             const month = months[key];

//             // ✅ borne globale (inchangé)
//             if (
//               month.year < (minDate as number) ||
//               month.year > (maxDate as number) ||
//               (month.year === minDate && month.month < startMonth) ||
//               (month.year === maxDate && month.month > endMonth)
//             ) {
//               continue;
//             }

//             // ✅ NOUVEAU : on ne prend pas les mois futurs pour type !== 1
//             if (type !== 1 && isFutureMonth(month.year, month.month)) {
//               continue;
//             }

//             projects.forEach((project) => {
//               // ✅ filtre par range de chaque projet
//               if (!isWithinProjectRange(month.year, month.month, project)) {
//                 return;
//               }

//               project.indicators.forEach((pi: any) => {
//                 if (pi.code === indicator.code) {
//                   const values = project.projectIndicatorDefaultValues.find(
//                     (dv: any) => dv.indicator.id === indicator.id
//                   );

//                   // Valeur de défaut auto (type 1 uniquement) — inchangé
//                   let arrayHas = project.projectIndicators.filter(
//                     (projectIndicator: any) =>
//                       projectIndicator.month == month.month &&
//                       projectIndicator.year == month.year &&
//                       projectIndicator.indicator.id == indicator.id &&
//                       projectIndicator.type == type
//                   );

//                   if (type === 1 && arrayHas.length === 0 && values) {
//                     const freq =
//                       values.frequency ?? indicator.defaultValueFrequency ?? 1;

//                     if (freq === 1) {
//                       arrayHas = [
//                         {
//                           id: -1,
//                           indicator: indicator,
//                           month: month.month,
//                           year: month.year,
//                           type: 1,
//                           value: values.value ?? 0,
//                         },
//                       ];
//                     } else if (freq === 2) {
//                       if (month.month % 3 === 0) {
//                         arrayHas = [
//                           {
//                             id: -1,
//                             indicator: indicator,
//                             month: month.month,
//                             year: month.year,
//                             type: 1,
//                             value: values.value ?? 0,
//                           },
//                         ];
//                       }
//                     } else if (freq === 3) {
//                       if (month.month === 0) {
//                         arrayHas = [
//                           {
//                             id: -1,
//                             indicator: indicator,
//                             month: month.month,
//                             year: month.year,
//                             type: 1,
//                             value: values.value ?? 0,
//                           },
//                         ];
//                       }
//                     }
//                   }

//                   if (month.year != lastLoopYear) {
//                     totalYear = 0;
//                   }

//                   let v = 0;
//                   if (arrayHas.length > 0) {
//                     const firstElement = arrayHas[0];
//                     v = firstElement.value;
//                   }

//                   lastValue += v;
//                   totalYear = lastValue;

//                   projectIndicators.push({
//                     id: 0,
//                     indicator: indicator,
//                     month: month.month,
//                     year: month.year,
//                     type: type,
//                     value: indicator.cumulative ? lastValue : v,
//                     yearValue: totalYear,
//                     maxDate:
//                       indicator.code === "arbres_plantes"
//                         ? (minDate as number) + maxTco2Year
//                         : null,
//                   });

//                   if (
//                     !projectTotalIndicators.find(
//                       (pti: any) =>
//                         pti.indicator.id === indicator.id && pti.type === type
//                     )
//                   ) {
//                     projectTotalIndicators.push({
//                       indicator: indicator,
//                       type: type,
//                       total: 0,
//                       totalYear: (maxDate as number) - (minDate as number) + 1,
//                     });
//                   }

//                   projectTotalIndicators.forEach((pti: any) => {
//                     if (
//                       pti.indicator.id === indicator.id &&
//                       pti.type === type
//                     ) {
//                       pti.total += v;
//                     }
//                   });

//                   if (!hasData.includes(indicator.id) && lastValue > 0) {
//                     hasData.push(indicator.id);
//                   }

//                   if ("arbres_plantes" === indicator.code) {
//                     if (totalArbres[project.id] === undefined) {
//                       totalArbres[project.id] = [];
//                     }
//                     if (totalArbres[project.id][month.year] === undefined) {
//                       totalArbres[project.id][month.year] = 0;
//                     }
//                     totalArbres[project.id][month.year] += v;
//                   }
//                 }
//               });
//             });

//             lastLoopYear = month.year;
//           }
//         } else {
//           maxTco2Year = 0;
//           // ----- tCO2 séquestrées -----
//           for (let key = 0; key < monthsTco2.length; key++) {
//             const month = monthsTco2[key];

//             // ⛏️ Avant : if (type != 1 && month.year > currentYear) continue;
//             // ✅ Maintenant : coupe aussi par mois
//             if (type !== 1 && isFutureMonth(month.year, month.month)) {
//               continue;
//             }

//             projects.forEach((project) => {
//               let tco2Unit: number | null = null;
//               let tco2Year: number | null = null;

//               data.parameter.default.forEach((p: any) => {
//                 if (p.tco2_unit !== undefined && p.tco2_unit !== null) {
//                   tco2Unit = p.tco2_unit;
//                 }
//                 if (p.tco2_year !== undefined && p.tco2_year !== null) {
//                   tco2Year = p.tco2_year;
//                 }
//               });

//               data.parameter.custom.forEach((p: any) => {
//                 if (
//                   p.tco2_unit !== undefined &&
//                   p.tco2_unit !== null &&
//                   p.project == project.id
//                 ) {
//                   tco2Unit = p.tco2_unit;
//                 }
//                 if (
//                   p.tco2_year !== undefined &&
//                   p.tco2_year !== null &&
//                   p.project == project.id
//                 ) {
//                   tco2Year = p.tco2_year;
//                 }
//               });

//               if (
//                 tco2Year !== null &&
//                 tco2Year > maxTco2Year &&
//                 totalArbres[project.id] !== undefined &&
//                 totArbres(totalArbres[project.id]) > 0
//               ) {
//                 maxTco2Year = parseInt(String(tco2Year), 10);
//               }

//               // ✅ filtre par range de chaque projet (aussi pour tCO2)
//               if (
//                 !isWithinProjectRange(
//                   month.year,
//                   month.month,
//                   project,
//                   tco2Year ?? 0
//                 )
//               ) {
//                 return;
//               }

//               let t = 0;
//               if (totalArbres[project.id] !== undefined) {
//                 totalArbres[project.id].forEach((v, y) => {
//                   if (y <= month.year) {
//                     t += v;
//                   }
//                 });
//               }

//               // totalYear = somme annuelle (pas par mois) — inchangé
//               if (!alreadyAdd.find((ad) => ad == month.year)) {
//                 totalYear += (tco2Unit ?? 0.018) * t;
//                 alreadyAdd.push(month.year);
//               }

//               projectIndicators.push({
//                 id: 0,
//                 indicator: indicator,
//                 month: month.month,
//                 year: month.year,
//                 type: type,
//                 value: totalYear ? totalYear / 12 : 0,
//                 yearValue: totalYear ? totalYear : 0,
//                 unitValue: tco2Unit,
//                 totalArbres:
//                   totalArbres[project.id] === undefined
//                     ? 0
//                     : totalArbres[project.id],
//                 t: t,
//                 maxDate: (minDate as number) + maxTco2Year,
//               });

//               if (
//                 t !== 0 &&
//                 !projectTotalIndicators.find(
//                   (pti: any) =>
//                     pti.indicator.id === indicator.id && pti.type === type
//                 )
//               ) {
//                 projectTotalIndicators.push({
//                   indicator: indicator,
//                   type: type,
//                   total: 0,
//                   totalYear: maxTco2Year,
//                 });
//               }

//               projectTotalIndicators.map((pti: any) => {
//                 if (pti.indicator.id === indicator.id && pti.type === type) {
//                   pti.total = totalYear ? Math.round(totalYear) : 0;
//                 }
//                 return pti;
//               });

//               if (!hasData.includes(indicator.id) && totalYear) {
//                 hasData.push(indicator.id);
//               }
//             });
//           }
//         }
//       }
//     }
//   });

//   return {
//     projectIndicators,
//     projectTotalIndicators,
//     hasData,
//     maxTco2Year: maxTco2Year - 1,
//   };
// };

export const calculReportingProject = (
  data: any,
  type: number | null = null, // 1 = Prévu (autorise futur), autres = Réalisé/Autres (pas futur)
  projectSelected: any[] = [],
  isForProject = false,
  context: "project" | "dashboard" | "programme" | null
) => {
  let minDate: number | null = null;
  let maxDate: number | null = null;
  let startMonth = 0;
  let endMonth = 11;
  let monthsTco2All: { year: number; month: number }[] = [];

  function getDatePeriodByProjectInfo(
    startYear: number,
    endYear: number,
    startMonth = 0,
    endMonth = 11
  ) {
    const months: { year: number; month: number }[] = [];
    for (let year = startYear; year <= endYear; year++) {
      const monthStart = year === startYear ? startMonth : 0;
      const monthEnd = year === endYear ? endMonth : 11;
      for (let month = monthStart; month <= monthEnd; month++) {
        months.push({ year, month });
      }
    }
    return months;
  }

  const ymToIndex = (y: number, m: number) => y * 12 + m;
  const cmpYM = (aY: number, aM: number, bY: number, bM: number) =>
    ymToIndex(aY, aM) - ymToIndex(bY, bM);

  function isWithinProjectRange(
    y: number,
    m: number,
    project: {
      launchDate?: string | Date | null;
      expectedDuration?: number | null;
      endDate?: string | Date | null;
    },
    tco2Year: number = 0
  ): boolean {
    if (!project?.launchDate) return true;

    const launch = new Date(project.launchDate);
    if (Number.isNaN(launch.getTime())) return true;

    const minIndex = ymToIndex(launch.getFullYear(), launch.getMonth());
    const curIndex = ymToIndex(y, m);

    const duration = tco2Year
      ? (tco2Year - 1) * 12
      : Number(project.expectedDuration ?? 0);

    if (!duration || duration <= 0) {
      return curIndex >= minIndex;
    }

    const maxIndex = minIndex + (duration - 1);
    return curIndex >= minIndex && curIndex <= maxIndex;
  }

  // Aujourd’hui
  const now = new Date();
  const nowY = now.getFullYear();
  const nowM = now.getMonth();
  const isFutureMonth = (y: number, m: number) =>
    y > nowY || (y === nowY && m > nowM);

  // Fenêtre globale (non tCO2)
  if (isForProject === true) {
    const project = projectSelected?.[0];

    if (project?.launchDate && project?.expectedDuration) {
      const startDate = new Date(project.launchDate);
      const monthsToAdd = Number(project.expectedDuration);

      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + monthsToAdd);
      endDate.setDate(0);

      minDate = startDate.getFullYear();
      maxDate = endDate.getFullYear();
      startMonth = startDate.getMonth();
      endMonth = endDate.getMonth();
    } else if (project?.launchDate && project?.endDate) {
      const startDate = new Date(project.launchDate);
      const endDate = new Date(project.endDate);

      minDate = startDate.getFullYear();
      maxDate = endDate.getFullYear();
      startMonth = startDate.getMonth();
      endMonth = endDate.getMonth();
    }
  } else {
    (data?.programs ?? []).forEach((program: any) => {
      (program?.projects ?? []).forEach((project: any) => {
        const launchDate = project?.launchDate
          ? new Date(project.launchDate)
          : null;
        if (launchDate) {
          const ly = launchDate.getFullYear();
          const lm = launchDate.getMonth();
          if (
            minDate === null ||
            ly < (minDate as number) ||
            (ly === minDate && lm < startMonth)
          ) {
            minDate = ly;
            startMonth = lm;
          }
        }

        let projectEndDate: Date | null = null;
        if (project?.expectedDuration && launchDate) {
          const ed = new Date(launchDate);
          ed.setMonth(ed.getMonth() + Number(project.expectedDuration));
          ed.setDate(0);
          projectEndDate = ed;
        } else if (project?.endDate) {
          projectEndDate = new Date(project.endDate);
        }

        if (projectEndDate) {
          const ey = projectEndDate.getFullYear();
          const em = projectEndDate.getMonth();
          if (
            maxDate === null ||
            ey > (maxDate as number) ||
            (ey === maxDate && em > endMonth)
          ) {
            maxDate = ey;
            endMonth = em;
          }
        }
      });
    });
  }

  const months = getDatePeriodByProjectInfo(
    minDate as number,
    maxDate as number,
    startMonth,
    endMonth
  );

  const types = [1, 2, 3];
  const projectIndicators: any[] = [];
  const projectTotalIndicators: any[] = [];
  const hasData: number[] = [];
  const projects: any[] = [];

  // Collecte projets + max tco2Year (pour compat maxDate affichage)
  let maxTco2Year = 0;

  data.programs.forEach((program: any) => {
    program.projects.forEach((project: any) => {
      if (
        (projectSelected.length &&
          !projectSelected.find((ps: any) => ps.id === project.id)) ||
        project.status !== 3
      )
        return;

      projects.push(project);

      let tco2Year: number | null = null;
      data.parameter.default.forEach((p: any) => {
        if (p.tco2_year !== undefined && p.tco2_year !== null) {
          tco2Year = parseInt(p.tco2_year);
        }
      });

      data.parameter.custom.forEach((p: any) => {
        if (
          p.tco2_year !== undefined &&
          p.tco2_year !== null &&
          p.project == project.id
        ) {
          tco2Year = parseInt(p.tco2_year);
        }
      });

      if (tco2Year !== null && tco2Year > maxTco2Year) {
        maxTco2Year = parseInt(String(tco2Year), 10);
      }
    });
  });

  // Helper: params tCO2 (unit, years) pour un projet
  function getTco2ParamsForProject(projectId: number) {
    let tco2Unit: number | null = null;
    let tco2Year: number | null = null;

    data.parameter.default.forEach((p: any) => {
      if (p.tco2_unit !== undefined && p.tco2_unit !== null)
        tco2Unit = p.tco2_unit;
      if (p.tco2_year !== undefined && p.tco2_year !== null)
        tco2Year = p.tco2_year;
    });
    data.parameter.custom.forEach((p: any) => {
      if (p.project == projectId) {
        if (p.tco2_unit !== undefined && p.tco2_unit !== null)
          tco2Unit = p.tco2_unit;
        if (p.tco2_year !== undefined && p.tco2_year !== null)
          tco2Year = p.tco2_year;
      }
    });

    return { unit: tco2Unit ?? 0.018, years: tco2Year ?? 0 };
  }

  // ===== Calcul par type =====
  types.forEach((t) => {
    const includeFutureForTrees = t === 1; // Prévu inclut futur; autres non.

    // ========= Pré-collecte arbres par projet/année (selon le type) =========
    const totalArbres: number[][] = [];
    const arbresIndicator = data.indicators.find(
      (ind: any) => ind.code === "arbres_plantes"
    );

    for (const project of projects) {
      if (!arbresIndicator) break;

      for (const month of months) {
        if (!includeFutureForTrees && isFutureMonth(month.year, month.month)) {
          continue; // pas d’arbres futurs pour t != 1
        }

        let v = 0;
        // Données du type courant
        const launchDate = project.launchDate;
        const mLaunch = launchDate ? new Date(launchDate).getMonth() : 0;
        const yLaunch = launchDate ? new Date(launchDate).getFullYear() : 0;

        if (month.year >= yLaunch && month.month >= mLaunch) {
          const arrayHas = project.projectIndicators?.filter(
            (pi: any) =>
              pi.month === month.month &&
              pi.year === month.year &&
              pi.indicator.id === arbresIndicator.id &&
              pi.type === t
          );

          if (arrayHas?.length) {
            v = arrayHas.reduce(
              (sum: number, item: any) => sum + (item?.value ?? 0),
              0
            );
          } else if (t === 1) {
            // Pour PREVU uniquement: fallback défauts (fréquence)
            const values = project.projectIndicatorDefaultValues?.find(
              (dv: any) => dv.indicator.id === arbresIndicator.id
            );
            if (values) {
              const freq =
                values.frequency ?? arbresIndicator.defaultValueFrequency ?? 1;
              const ok =
                freq === 1 ||
                (freq === 2 && month.month % 3 === 0) ||
                (freq === 3 && month.month === 0);
              if (ok) v = values.value ?? 0;
            }
          }
        }

        if (!totalArbres[project.id]) totalArbres[project.id] = [];
        if (totalArbres[project.id][month.year] === undefined)
          totalArbres[project.id][month.year] = 0;
        totalArbres[project.id][month.year] += v;
      }
    }

    // ========= Plages tCO2 PAR PROJET (avec yearsAdj = years - 1) =========
    type ProjTco2Range = {
      id: number;
      startY: number;
      startM: number;
      endY: number;
      endM: number;
      unit: number;
      years: number; // original
      yearsAdj: number; // years - 1
    };

    const projRanges: ProjTco2Range[] = [];
    let globalTco2StartY = Number.MAX_SAFE_INTEGER;
    let globalTco2StartM = 11;
    let globalTco2EndY = -1;
    let globalTco2EndM = 0;

    for (const project of projects) {
      const { unit, years } = getTco2ParamsForProject(project.id);
      const launch = project?.launchDate ? new Date(project.launchDate) : null;
      if (!launch || Number.isNaN(launch.getTime()) || !years) continue;

      const sY = launch.getFullYear();
      const sM = launch.getMonth();

      // Ajustement : retirer 1 an (car launchYear est inclus)
      const yearsAdj = Math.max(0, years - 1);

      const end = new Date(launch);
      end.setMonth(end.getMonth() + yearsAdj * 12);
      end.setDate(0);
      const eY = end.getFullYear();
      const eM = end.getMonth();

      projRanges.push({
        id: project.id,
        startY: sY,
        startM: sM,
        endY: eY,
        endM: eM,
        unit,
        years,
        yearsAdj,
      });

      if (cmpYM(sY, sM, globalTco2StartY, globalTco2StartM) < 0) {
        globalTco2StartY = sY;
        globalTco2StartM = sM;
      }
      if (cmpYM(eY, eM, globalTco2EndY, globalTco2EndM) > 0) {
        globalTco2EndY = eY;
        globalTco2EndM = eM;
      }
    }

    monthsTco2All =
      globalTco2EndY >= 0
        ? getDatePeriodByProjectInfo(
          globalTco2StartY,
          globalTco2EndY,
          globalTco2StartM,
          globalTco2EndM
        )
        : months;

    // Helper: total attendu tCO2 d'un projet (somme par année: unit * arbres_cumulés)
    // (Conservée si tu veux l’utiliser ailleurs, mais plus utilisée pour le total.)
    function expectedTco2TotalForProject(
      projectId: number,
      unit: number,
      sY: number,
      eY: number
    ): number {
      let total = 0;
      let cumulative = 0;
      for (let y = sY; y <= eY; y++) {
        const plantedThisYear =
          totalArbres[projectId] && totalArbres[projectId][y]
            ? totalArbres[projectId][y]
            : 0;
        cumulative += plantedThisYear;
        total += unit * cumulative;
      }
      return total; // pas d'arrondi
    }

    // ================= INDICATEURS =================
    for (let keyi = 0; keyi < data.indicators.length; keyi++) {
      const indicator = data.indicators[keyi];

      // Préparer la ligne "total" pour non-tCO2
      if (
        indicator.code !== "kg_co2_arbre_an" &&
        indicator.code !== "tonne_co2_ha_an" &&
        indicator.code !== "type-d-arrosage" &&
        indicator.code !== "tco2_sequestrees" &&
        !projectTotalIndicators.find(
          (pti: any) => pti.indicator.id === indicator.id && pti.type === t
        )
      ) {
        projectTotalIndicators.push({
          indicator: indicator,
          type: t,
          total: 0,
          totalYear: (maxDate as number) - (minDate as number) + 1,
          count: 0,
        });
      }

      if (indicator.code !== "tco2_sequestrees") {
        // ---------- Valeurs brutes annuelles, sans cumul ----------
        let totalYear = 0;
        let lastLoopYear = 0;
        let countableValue = 0;

        for (const month of months) {
          if (
            month.year < (minDate as number) ||
            month.year > (maxDate as number) ||
            (month.year === minDate && month.month < startMonth) ||
            (month.year === maxDate && month.month > endMonth)
          ) {
            continue;
          }
          if (t !== 1 && isFutureMonth(month.year, month.month)) {
            continue;
          }

          projects.forEach((project) => {
            if (!isWithinProjectRange(month.year, month.month, project)) return;

            project.indicators.forEach((pi: any) => {
              if (pi.code !== indicator.code) return;

              const values = project.projectIndicatorDefaultValues?.find(
                (dv: any) => dv.indicator.id === indicator.id
              );

              let arrayHas = project.projectIndicators?.filter(
                (projectIndicator: any) =>
                  projectIndicator.month == month.month &&
                  projectIndicator.year == month.year &&
                  projectIndicator.indicator.id == indicator.id &&
                  projectIndicator.type == t
              );

              if (t === 1 && (!arrayHas || arrayHas.length === 0) && values) {
                const freq =
                  values.frequency ?? indicator.defaultValueFrequency ?? 1;

                const ok =
                  freq === 1 ||
                  (freq === 2 && month.month % 3 === 0) ||
                  (freq === 3 && month.month === 0);

                if (ok) {
                  arrayHas = [
                    {
                      id: -1,
                      indicator: indicator,
                      month: month.month,
                      year: month.year,
                      type: 1,
                      value: values.value ?? 0,
                    },
                  ];
                }
              }

              if (month.year !== lastLoopYear) {
                totalYear = 0;
              }
              lastLoopYear = month.year;

              let hasvalue = false;
              let v = 0;
              if (arrayHas && arrayHas.length > 0) {
                v = arrayHas.reduce(
                  (sum: number, item: any) => sum + (item?.value ?? 0),
                  0
                );
                hasvalue = arrayHas.some((item: any) => item?.value !== null);
                countableValue++;
              }
              totalYear += v;
              projectIndicators.push({
                id: 0,
                indicator: indicator,
                month: month.month,
                year: month.year,
                type: t,
                value: v,
                yearValue: totalYear,
                maxDate:
                  indicator.code === "arbres_plantes"
                    ? (minDate as number) + maxTco2Year
                    : null,
                valuewithoutCumul: hasvalue ? v : null,
              });

              const totalRow = projectTotalIndicators.find(
                (pti: any) =>
                  pti.indicator.id === indicator.id && pti.type === t
              );
              if (totalRow) {
                totalRow.total += v;

                if (indicator.moyenne && countableValue) {
                  totalRow.count = countableValue;
                }
              }

              if (!hasData.includes(indicator.id) && v > 0) {
                hasData.push(indicator.id);
              }
            });
          });

          // lastLoopYear = month.year;
        }
      } else {
        // ========== tCO2 : agrégation brute par année, sans cumul inter-années ==========
        // Ligne total tCO2 (par type) si absente
        if (
          !projectTotalIndicators.find(
            (pti: any) => pti.indicator.id === indicator.id && pti.type === t
          )
        ) {
          const tYears =
            monthsTco2All.length > 0
              ? monthsTco2All[monthsTco2All.length - 1].year -
              monthsTco2All[0].year +
              1
              : 0;

          projectTotalIndicators.push({
            indicator: indicator,
            type: t,
            total: 0,
            totalYear: tYears,
            count: 0,
          });
        }
        const totalRow = projectTotalIndicators.find(
          (pti: any) => pti.indicator.id === indicator.id && pti.type === t
        );

        // Flags intra-année par projet pour éviter le double comptage
        const addedYearByProject: Record<number, Record<number, boolean>> = {};
        // Totaux annuels (tous projets) pour chaque année
        const yearTotalAcrossProjects: Record<number, number> = {};

        // >>> Boucle d'abord sur les months (fenêtre tCO2 globale) <<<
        for (const month of monthsTco2All) {
          if (t !== 1 && isFutureMonth(month.year, month.month)) continue;

          // Ensuite, pour chaque projet : contribution annuelle une seule fois
          projects.forEach((project) => {
            const pr = projRanges.find((r) => r.id === project.id);
            if (!pr) return;

            // Mois dans la fenêtre tCO2 du projet ?
            const inRange =
              cmpYM(month.year, month.month, pr.startY, pr.startM) >= 0 &&
              cmpYM(month.year, month.month, pr.endY, pr.endM) <= 0;
            if (!inRange) return;

            // arbres cumulés jusqu'à l'année courante (selon collecte du type courant)
            let tCum = 0;
            if (totalArbres[project.id] !== undefined) {
              for (const yStr of Object.keys(totalArbres[project.id])) {
                const y = parseInt(yStr, 10);
                if (y <= month.year) tCum += totalArbres[project.id][y] || 0;
              }
            }

            // Ajout au plus une fois par (projet, année)
            if (!addedYearByProject[project.id])
              addedYearByProject[project.id] = {};
            if (!addedYearByProject[project.id][month.year]) {
              const contrib = pr.unit * tCum;
              addedYearByProject[project.id][month.year] = true;

              if (yearTotalAcrossProjects[month.year] === undefined)
                yearTotalAcrossProjects[month.year] = 0;
              yearTotalAcrossProjects[month.year] += contrib;
            }
          });

          const yearValue = yearTotalAcrossProjects[month.year] || 0;

          projectIndicators.push({
            id: 0,
            indicator,
            month: month.month,
            year: month.year,
            type: t,
            value: yearValue ? yearValue / 12 : 0,
            yearValue: yearValue,
            unitValue: null,
            totalArbres: null,
            t: null,
            maxDate: (minDate as number) + maxTco2Year,
            valueWithoutCummul: null,
          });

          if (!hasData.includes(indicator.id) && yearValue) {
            hasData.push(indicator.id);
          }
        }

        // ✅ TOTAL tCO₂ CALÉ SUR LA SÉRIE POUR TOUS LES TYPES
        if (totalRow) {
          const sumYears = Object.values(yearTotalAcrossProjects).reduce(
            (acc, v) => acc + v,
            0
          );
          totalRow.total = sumYears; // pas d'arrondi
          if (indicator?.moyenne) {
            totalRow.count = (totalRow.count ?? 0) + 1;
          }
        }
      }
    }
  });
  // console.log("monthsTco2All", monthsTco2All);
  for (const pti of projectTotalIndicators) {
    const uni = pti?.indicator?.moyenne;
    if (uni) {
      if (pti.count > 0) {
        pti.total = pti.total / pti.count;
      }
    }
  }

  return {
    projectIndicators,
    projectTotalIndicators,
    hasData,
    maxTco2Year:
      monthsTco2All.length > 0 && monthsTco2All[monthsTco2All.length - 1].year,
  };
};

export const calculReportingGraph = (
  data,
  type = null,
  projectSelected = [],
  context = null
) => {
  const currentYear = new Date().getFullYear();
  const minDate = parseInt(data.minDate);
  const maxDate = parseInt(data.maxDate);

  const monthsOther = getDatePeriod(minDate, maxDate);
  const months = monthsOther;

  const types = [1, 2, 3];
  const projectIndicators = [];
  const projectTotalIndicators = [];
  const hasData = [];
  const projects = [];

  let maxTco2Year = 0;

  let allIndicators = [];

  // --- Récupération des projets et du maxTco2Year ---
  data.programs.forEach((program) => {
    program.projects.forEach((project) => {
      // merge project.indicators to allIndicators with no repetition
      allIndicators = allIndicators.concat(
        project.indicators.filter(
          (ind) => !allIndicators.some((a) => a.id === ind.id)
        )
      );

      if (
        (type && project.type.type !== type) ||
        (projectSelected.length &&
          !projectSelected.find((ps) => ps.id === project.id))
      ) {
        return;
      }

      projects.push(project);

      // paramètres par défaut
      data.parameter.default.forEach((p) => {
        if (p.tco2_year !== undefined && p.tco2_year !== null) {
          maxTco2Year = Math.max(maxTco2Year, parseInt(p.tco2_year));
        }
      });

      // paramètres spécifiques au projet
      data.parameter.custom.forEach((p) => {
        if (
          p.project === project.id &&
          p.tco2_year !== undefined &&
          p.tco2_year !== null
        ) {
          maxTco2Year = Math.max(maxTco2Year, parseInt(p.tco2_year));
        }
      });
    });
  });

  const monthsTco2 = getDatePeriod(minDate, minDate + maxTco2Year);

  // --- Boucle sur les types ---
  types.forEach((currentType) => {
    const totalArbres = [];

    for (let keyi = 0; keyi < data.indicators.length; keyi++) {
      const indicator = data.indicators[keyi];

      // Filtrage par contexte
      if (
        (context === "dashboard" &&
          !indicator.dashboard &&
          indicator.code !== "arbres_plantes") ||
        (context === "programme" &&
          !indicator.programme &&
          indicator.code !== "arbres_plantes")
      ) {
        continue;
      }

      // Initialisation projectTotalIndicators
      projectTotalIndicators.push({
        indicator,
        type: currentType,
        total: 0,
        totalYear: maxDate - minDate + 1,
        maxDate:
          indicator.code === "arbres_plantes" ? minDate + maxTco2Year : null,
      });

      // Exclure certains indicateurs
      if (
        indicator.code === "kg_co2_arbre_an" ||
        indicator.code === "tonne_co2_ha_an" ||
        indicator.code === "type-d-arrosage"
      ) {
        continue;
      }

      let lastDefaultValue = 0;
      let lastValue = 0;
      let totalYear = 0;
      let allTotalYear = 0;
      let lastLoopYear = 0;
      const alreadyAdd = [];

      // --- Cas spécifique : indicateur tco2_sequestrees ---
      if (indicator.code === "tco2_sequestrees") {
        for (const month of monthsTco2) {
          if (currentType !== 1 && month.year > currentYear) continue;

          projects.forEach((project) => {
            let tco2Unit = null;

            // paramètres par défaut
            data.parameter.default.forEach((p) => {
              if (p.tco2_unit !== undefined && p.tco2_unit !== null)
                tco2Unit = p.tco2_unit;
            });

            // paramètres custom
            data.parameter.custom.forEach((p) => {
              if (
                p.project === project.id &&
                p.tco2_unit !== undefined &&
                p.tco2_unit !== null
              ) {
                tco2Unit = p.tco2_unit;
              }
            });

            let t = 0;
            totalArbres.forEach((v, y) => {
              if (y <= month.year) t += v;
            });

            if (!alreadyAdd.includes(month.year)) {
              totalYear += tco2Unit * t;
              alreadyAdd.push(month.year);
            }

            projectIndicators.push({
              id: 0,
              indicator,
              month: month.month,
              year: month.year,
              type: currentType,
              value: totalYear ? totalYear / 12 : 0,
              yearValue: totalYear || 0,
              unitValue: tco2Unit,
              totalArbres,
              t,
              maxDate: minDate + maxTco2Year,
            });

            if (
              t !== 0 &&
              !projectTotalIndicators.find(
                (pti) =>
                  pti.indicator.id === indicator.id && pti.type === currentType
              )
            ) {
              projectTotalIndicators.push({
                indicator,
                type: currentType,
                total: 0,
                totalYear: maxTco2Year,
              });
            }

            projectTotalIndicators.forEach((pti) => {
              if (
                pti.indicator.id === indicator.id &&
                pti.type === currentType
              ) {
                pti.total = totalYear ? Math.round(totalYear) : 0;
              }
            });

            if (!hasData.includes(indicator.id) && totalYear) {
              hasData.push(indicator.id);
            }
          });
        }
        continue; // fin du cas tco2_sequestrees
      }

      // --- Cas général ---
      for (let key = 0; key < months.length; key++) {
        const month = months[key];
        if (currentType !== 1 && month.year > currentYear) continue;

        projects.forEach((project) => {
          const [launchYear, launchMonthRaw] = project.launchDate
            ? project.launchDate.split("-")
            : [1970, 1];
          const launchYearInt = parseInt(launchYear);
          const launchMonthInt = parseInt(launchMonthRaw) - 1;

          project.indicators.forEach((pi) => {
            if (pi.code !== indicator.code) return;

            const arrayHas = project.projectIndicators.filter(
              (projectIndicator) =>
                projectIndicator.month === month.month &&
                projectIndicator.year === month.year &&
                projectIndicator.indicator.id === indicator.id &&
                projectIndicator.type === currentType
            );

            if (month.year !== lastLoopYear) {
              totalYear = 0;
            }

            // --- Estimés (type = 1) ---
            if (currentType === 1) {
              const values = project.projectIndicatorDefaultValues.filter(
                (dv) => dv.indicator.id === indicator.id
              );

              let defaultValue = 0;
              const hasDefault =
                values.length > 0 &&
                (month.year > launchYearInt ||
                  (month.year === launchYearInt &&
                    month.month >= launchMonthInt));

              if (hasDefault) {
                defaultValue = values[0].value || 0;

                // --- valeur par défaut sans donnée réelle ---
                if (defaultValue && arrayHas.length === 0) {
                  const frequency = values.frequency;

                  if (lastDefaultValue === 0 || !cumulOptions) {
                    lastDefaultValue = defaultValue;
                    totalYear = defaultValue;
                  } else {
                    switch (frequency) {
                      case 2: // trimestriel
                        if (key % 3 === 0) {
                          lastDefaultValue += defaultValue;
                          totalYear += defaultValue;
                        }
                        break;
                      case 3: // annuel
                        if (key % 12 === 0) {
                          lastDefaultValue += defaultValue;
                          totalYear += defaultValue;
                        }
                        break;
                      default: // mensuel
                        lastDefaultValue += defaultValue;
                        totalYear += defaultValue;
                        break;
                    }
                  }

                  projectIndicators.push({
                    id: 0,
                    indicator,
                    month: month.month,
                    year: month.year,
                    type: currentType,
                    value: cumulOptions
                      ? lastDefaultValue
                      : defaultValue,
                    yearValue: totalYear,
                    maxDate:
                      indicator.code === "arbres_plantes"
                        ? minDate + maxTco2Year
                        : null,
                  });

                  if (
                    !projectTotalIndicators.find(
                      (pti) =>
                        pti.indicator.id === indicator.id &&
                        pti.type === currentType
                    )
                  ) {
                    projectTotalIndicators.push({
                      indicator,
                      type: currentType,
                      total: 0,
                      totalYear: maxDate - minDate + 1,
                      maxDate:
                        indicator.code === "arbres_plantes"
                          ? minDate + maxTco2Year
                          : null,
                    });
                  }

                  projectTotalIndicators.forEach((pti) => {
                    if (
                      pti.indicator.id === indicator.id &&
                      pti.type === currentType
                    ) {
                      pti.total += defaultValue;
                    }
                  });
                }

                // --- données réelles ---
                else if (arrayHas.length > 0) {
                  const { value } = arrayHas[0];
                  lastDefaultValue += value;
                  totalYear += value;
                  allTotalYear += value;

                  if (!cumulOptions && month.year !== lastLoopYear) {
                    totalYear = 0;
                  }

                  projectIndicators.push({
                    id: 0,
                    indicator,
                    month: month.month,
                    year: month.year,
                    type: currentType,
                    value: cumulOptions ? lastDefaultValue : value,
                    yearValue: totalYear,
                  });

                  if (
                    !projectTotalIndicators.find(
                      (pti) =>
                        pti.indicator.id === indicator.id &&
                        pti.type === currentType
                    )
                  ) {
                    projectTotalIndicators.push({
                      indicator,
                      type: currentType,
                      total: 0,
                      totalYear: maxDate - minDate + 1,
                    });
                  }

                  projectTotalIndicators.forEach((pti) => {
                    if (
                      pti.indicator.id === indicator.id &&
                      pti.type === currentType
                    ) {
                      pti.total += value;
                    }
                  });

                  if (!hasData.includes(indicator.id)) {
                    hasData.push(indicator.id);
                  }

                  if (indicator.code === "arbres_plantes") {
                    totalArbres[month.year] =
                      (totalArbres[month.year] || 0) + value;
                  }
                }

                // --- aucune donnée => reprendre valeur par défaut ---
                else {
                  const valueToUse = defaultValue || 0;
                  lastDefaultValue += valueToUse;
                  totalYear += valueToUse;
                  allTotalYear += valueToUse;

                  projectIndicators.push({
                    id: 0,
                    indicator,
                    month: month.month,
                    year: month.year,
                    type: currentType,
                    value: cumulOptions ? lastDefaultValue : valueToUse,
                    yearValue: totalYear,
                    maxDate:
                      indicator.code === "arbres_plantes"
                        ? minDate + maxTco2Year
                        : null,
                  });

                  if (!hasData.includes(indicator.id)) {
                    hasData.push(indicator.id);
                  }

                  if (
                    !projectTotalIndicators.find(
                      (pti) =>
                        pti.indicator.id === indicator.id &&
                        pti.type === currentType
                    )
                  ) {
                    projectTotalIndicators.push({
                      indicator,
                      type: currentType,
                      total: 0,
                      totalYear: maxDate - minDate + 1,
                    });
                  }

                  projectTotalIndicators.forEach((pti) => {
                    if (
                      pti.indicator.id === indicator.id &&
                      pti.type === currentType &&
                      currentType === 1
                    ) {
                      pti.total += valueToUse;
                    }
                  });

                  if (!cumulOptions && month.year !== lastLoopYear) {
                    totalYear = 0;
                  }

                  if (indicator.code === "arbres_plantes") {
                    totalArbres[month.year] =
                      (totalArbres[month.year] || 0) + valueToUse;
                  }
                }
              }

              // --- pas de valeur par défaut mais données réelles ---
              else if (arrayHas.length > 0) {
                const { value } = arrayHas[0];
                lastDefaultValue += value;
                totalYear = lastDefaultValue;

                if (!cumulOptions && month.year !== lastLoopYear) {
                  totalYear = 0;
                }

                projectIndicators.push({
                  id: 0,
                  indicator,
                  month: month.month,
                  year: month.year,
                  type: currentType,
                  value: cumulOptions ? lastDefaultValue : value,
                  yearValue: totalYear,
                  maxDate:
                    indicator.code === "arbres_plantes"
                      ? minDate + maxTco2Year
                      : null,
                });

                if (
                  !projectTotalIndicators.find(
                    (pti) =>
                      pti.indicator.id === indicator.id &&
                      pti.type === currentType
                  )
                ) {
                  projectTotalIndicators.push({
                    indicator,
                    type: currentType,
                    total: 0,
                    totalYear: maxDate - minDate + 1,
                    maxDate:
                      indicator.code === "arbres_plantes"
                        ? minDate + maxTco2Year
                        : null,
                  });
                }

                projectTotalIndicators.forEach((pti) => {
                  if (
                    pti.indicator.id === indicator.id &&
                    pti.type === currentType
                  ) {
                    pti.total += value;
                  }
                });

                if (!hasData.includes(indicator.id)) {
                  hasData.push(indicator.id);
                }

                if (indicator.code === "arbres_plantes") {
                  totalArbres[month.year] =
                    (totalArbres[month.year] || 0) + value;
                }
              }

              // --- aucune donnée du tout => push lastDefaultValue ---
              else {
                projectIndicators.push({
                  id: 0,
                  indicator,
                  month: month.month,
                  year: month.year,
                  type: currentType,
                  value: lastDefaultValue,
                  yearValue: totalYear,
                  maxDate:
                    indicator.code === "arbres_plantes"
                      ? minDate + maxTco2Year
                      : null,
                });
              }
            }

            // --- Autres types ---
            else {
              const v = arrayHas.length > 0 ? arrayHas[0].value : 0;
              lastValue += v;
              totalYear = lastValue;

              if (!cumulOptions && month.year !== lastLoopYear) {
                totalYear = 0;
              }

              projectIndicators.push({
                id: 0,
                indicator,
                month: month.month,
                year: month.year,
                type: currentType,
                value: cumulOptions ? lastValue : v,
                yearValue: totalYear,
                maxDate:
                  indicator.code === "arbres_plantes"
                    ? minDate + maxTco2Year
                    : null,
              });

              if (
                !projectTotalIndicators.find(
                  (pti) =>
                    pti.indicator.id === indicator.id &&
                    pti.type === currentType
                )
              ) {
                projectTotalIndicators.push({
                  indicator,
                  type: currentType,
                  total: 0,
                  totalYear: maxDate - minDate + 1,
                });
              }

              projectTotalIndicators.forEach((pti) => {
                if (
                  pti.indicator.id === indicator.id &&
                  pti.type === currentType
                ) {
                  pti.total += v;
                }
              });

              if (!hasData.includes(indicator.id) && lastValue > 0) {
                hasData.push(indicator.id);
              }

              if (indicator.code === "arbres_plantes") {
                totalArbres[month.year] = (totalArbres[month.year] || 0) + v;
              }
            }
          });
        });

        lastLoopYear = month.year;
      }
    }
  });

  return {
    allIndicators,
    projectIndicators,
    projectTotalIndicators,
    hasData,
    maxTco2Year,
  };
};

export const convertM2ToHectares = (m2: number): number => {
  return parseFloat((m2 / 10000).toFixed(2));
};

export function comparePolygons(poly1, poly2) {
  // Vérifie d'abord que le nombre d'anneaux (rings) est le même
  if (poly1.length !== poly2.length) return false;
  // Compare chaque anneau
  for (let i = 0; i < poly1.length; i++) {
    if (poly1[i].length !== poly2[i].length) return false;
    for (let j = 0; j < poly1[i].length; j++) {
      const [lat1, lng1] = poly1[i][j];
      const [lat2, lng2] = poly2[i][j];
      if (lat1 !== lat2 || lng1 !== lng2) return false;
    }
  }
  return true;
}

export const isImage = (ext: string) =>
  ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext);
export const isOffices = (ext: string) =>
  ["doc", "docx", "xlsx", "ods"].includes(ext);
